import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from './api';
import { PhoneCards } from './PhoneCards';
import { useIsPhone } from './useIsPhone';
import { normLocation, parseLocation } from './location';
import WhoIsWorking, { Person } from './pp/WhoIsWorking';
import { inventoryText } from './inventoryText';
import { checkBuild, reloadFresh, useBuildStale } from './buildCheck';
import { Catalogue, CatItem, LineState, QueuedScan, ScanQueue, ScanResult, colorOf, decideScan, getCatalogue, loadCatalogue,
         newCid, patchCatalogue, sectionDisplay, sectionKey, subscribeCatalogue } from './inventoryLocal';

// Inventory — the stock count of the hall, rebuilt 2026-10-06 (docs/INVENTORY.md). Sections are the floor
// plan's, grouped by aisle; the expected quantity is the shop's live stock + what the WMS has not picked
// yet; Motley and Nora count, Big Oliver never; "found here" writes the shelf back; a counted difference is
// approved by a manager on the Manager page before the relay writes it. Staff count on the tablets (a tablet
// asks who), managers and admins see Statistics and Manager. Texts in the counter's language (inventoryText).
// Review 6.10: ± keeps the scanner focused (its Enter used to click the button again), found-elsewhere rows
// are read-only everywhere, a section change resets the page, a session closed elsewhere is noticed.
// v3 (Fernando + Henrik's first count, 7–8.10): the laptop decides every scan itself against the Start snapshot
// and the hall map (inventoryLocal.ts) — the popup is there when the scanner beeps — and syncs in the background;
// "Ready to finish" shows the conclusion before "Save count"; Skip / Clear for the counter; Back/Forth by number.
// Rework (9.10, docs/INVENTORY.md): no manager step — Save count writes the section's ready items to the shops (the
// conclusion's Shop block shows what would be written / waits / is skipped, decided PER ITEM across the round);
// Pause / Continue and Cancel count; moving pieces before scanning; the RESTOCK tag; products grouped with a gap.

const INV = `${API}/wgr-inventory`;
type Tx = ReturnType<typeof inventoryText>;

// ── types ─────────────────────────────────────────────────────────────────────
interface Sec { code: string; display: string; pieces: number; done: boolean; last_checked_at: string | null; active: boolean; paused?: boolean; counted_by?: string | null;
                reserved: boolean; floor_space: boolean; alias: string | null; unit_id: string | null; letter?: string | null; number?: number | null }
interface Face { side: 'left' | 'right'; letter: string; sections: Sec[]; done: number; with_stock: number }
interface Aisle { name: string; box_label: string; active: boolean; walk_order: number | null; faces: Face[] }
/** The update gate (backend _gate): while on, no new section can be started; a running count goes on. */
interface Gate { on: boolean; minutes_left: number; message: string | null; fresh_client_required?: boolean }
interface Sections { round_id: number; aisles: Aisle[]; special: Sec[]; not_in_plan: Sec[]; sections_with_stock: number; sections_done: number; update_gate?: Gate }
interface Row {
  wms_id: number; webshop_id: string; platform: 'wgr' | 'shopify'; article_number: string | null; picklist_number: string | null;
  label: string; name: string | null; size: string | null; barcode: string | null; home: string | null; home_code: string | null;
  role: 'home' | 'overflow' | 'misplaced' | 'moved_away'; expected_qty: number; expected_raw: number | null; live_stock: number | null;
  unpicked_qty: number; in_flight_qty: number; stock_source: 'live' | 'sync' | null; infinite: boolean;
  scanned_saved_qty: number; ongoing_qty: number; overflow_qty: number; counted_total: number; color: string; fixed: boolean;
  line_id: number | null; resolution: string | null; write_status: string | null; found_at?: string;
  skipped?: boolean; skipped_by?: string | null; moved_out_qty?: number; moved_to?: string | null; moved_in_qty?: number; moved_in_from?: string | null;
  restock?: string[]; restock_codes?: string[]; master_sku?: string | null;
}
interface Detail {
  round_id: number; code: string; display: string; in_plan: boolean; reserved: boolean; floor_space: boolean; alias: string | null;
  aisle: string | null; unit_id: string | null; done: boolean; stock_source?: 'live' | 'sync';
  codes?: string[]; letter?: string | null; number?: number | null; snapshot_at?: string | null; saved_at?: string | null;
  active_session: { id: number; person: string; device: string | null; started_at: string; paused_at?: string | null } | null;
  article_count: number; expected_total: number; in_flight: number;
  rows: Row[]; overflow_rows: Row[]; extra_rows: Row[]; found_elsewhere: Row[];
  moves?: Move[];
  update_gate?: Gate;
}
interface Move { id: number; wms_id: number; label: string; size: string | null; status: 'pending' | 'applied'; qty: number; direction: 'in' | 'out' | 'here';
                 kind?: 'out' | 'here' | 'unscanned'; undoable?: boolean; note?: string | null;
                 from_code: string; to_code: string; from: string; to: string; by: string; created_at: string | null }
/** One item as the per-item decision sees it (backend _evaluate_one / _shop_row). */
interface Place { code: string; display: string; role: string; counted: number; done: boolean; being_counted_by: string | null; skipped: boolean }
interface Hold { code: string; text: string; places: string[] }
interface ShopRow { wms_id: number; label: string | null; size: string | null; platform: string | null; counted: number | null; expected: number | null;
                    difference: number | null; written: number; pending: number; delta: number; status: string; places: Place[]; holds: Hold[] }
interface Shop { mode: 'on' | 'off'; write: ShopRow[]; held: ShopRow[]; skipped: ShopRow[]; pending: ShopRow[]; written: ShopRow[]; review: ShopRow[];
                 nothing: number; infinite: number; plan_token: string; pieces_plus: number; pieces_minus: number;
                 later?: number; later_places?: string[] }

// ── helpers ───────────────────────────────────────────────────────────────────
const role = () => localStorage.getItem('wms_role') || 'staff';
const elevated = () => ['admin', 'manager'].includes(role());
const isDevice = () => !!localStorage.getItem('wms_is_device');
const me = () => localStorage.getItem('wms_username') || '';

/** An API error with its status and the server's `reason` (e.g. plan_changed) for the screens that act on it. */
function apiError(status: number, d: any): Error {
  const det = d?.detail;
  const e: any = new Error(typeof det === 'string' ? det : det && typeof det.detail === 'string' ? det.detail : `HTTP ${status}`);
  e.status = status; e.reason = det && typeof det === 'object' ? det.reason : undefined;
  return e;
}
async function jget(url: string) {
  const r = await authFetch(url);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiError(r.status, d);
  return d;
}
async function jpost(url: string, body: any) {
  const r = await authFetch(url, { method: 'POST', body: JSON.stringify(body ?? {}) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiError(r.status, d);
  return d;
}
const sign = (n: number | null | undefined) => n == null ? '—' : n > 0 ? `+${n}` : `${n}`;
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}
const SHOP: Record<string, string> = { wgr: 'Motley', shopify: 'Nora' };

const ROW_CLS: Record<string, string> = {
  white: 'border-l-gray-700', orange: 'border-l-orange-500 bg-orange-500/10', green: 'border-l-green-500 bg-green-600/15',
  blue: 'border-l-blue-400 bg-blue-600/15', red: 'border-l-red-500 bg-red-600/15', yellow: 'border-l-yellow-400 bg-yellow-500/15',
  purple: 'border-l-purple-400 bg-purple-600/15', gray: 'border-l-gray-400 bg-gray-500/15',
  skipped: 'border-l-teal-400 bg-teal-600/15',
};
const DOT: Record<string, string> = {
  white: 'bg-gray-500', orange: 'bg-orange-500', green: 'bg-green-500', blue: 'bg-blue-400', red: 'bg-red-500',
  yellow: 'bg-yellow-400', purple: 'bg-purple-400', gray: 'bg-gray-400', skipped: 'bg-teal-400',
};
const POPUP: Record<string, { bg: string; fg: string }> = {
  green: { bg: 'bg-green-600', fg: 'text-white' }, blue: { bg: 'bg-blue-600', fg: 'text-white' }, red: { bg: 'bg-red-600', fg: 'text-white' },
  yellow: { bg: 'bg-yellow-400', fg: 'text-black' }, gray: { bg: 'bg-white', fg: 'text-black' }, white: { bg: 'bg-white', fg: 'text-black' },
  orange: { bg: 'bg-white', fg: 'text-black' }, unknown_ean: { bg: 'bg-red-800', fg: 'text-white' }, ambiguous: { bg: 'bg-purple-700', fg: 'text-white' },
  skipped: { bg: 'bg-teal-600', fg: 'text-white' }, pending: { bg: 'bg-gray-700', fg: 'text-white' },
  correction: { bg: 'bg-orange-600', fg: 'text-white' }, stopped: { bg: 'bg-red-900', fg: 'text-white' },
};

/** The hall map in the page: loaded once, re-Load any time (Fernando, 8.10). */
function useCatalogue() {
  const [cat, setCat] = useState<Catalogue | null>(getCatalogue());
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(getCatalogue() ? 'ready' : 'idle');
  const [error, setError] = useState('');
  const reload = useCallback(async (force = true) => {
    setStatus('loading'); setError('');
    try { const c = await loadCatalogue(force); setCat(c); setStatus('ready'); }
    catch (e: any) { setError(e.message); setStatus(getCatalogue() ? 'ready' : 'error'); }
  }, []);
  useEffect(() => {
    const off = subscribeCatalogue(() => setCat(getCatalogue()));
    if (!getCatalogue()) reload(false);
    return off;
  }, [reload]);
  return { cat, status, error, reload };
}

function CatalogueChip({ tx }: { tx: Tx }) {
  const { cat, status, error, reload } = useCatalogue();
  const t = cat ? new Date(cat.loadedAt) : null;
  const hm = t ? `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}` : '';
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      {status === 'loading' && <span className="text-blue-300">{tx.loading}</span>}
      {status === 'error' && <span className="text-red-400">{error}</span>}
      {cat && status !== 'loading' && <span className="text-gray-500">{tx.loadedAt(hm, cat.count)}</span>}
      <button onClick={() => reload(true)} disabled={status === 'loading'} className="px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-40">{tx.load}</button>
    </span>
  );
}

/** Back = the lower number, Forth = the higher, within the same letter (Fernando, 8.10). The floor plan lists a
 *  right-hand face top-first, which is number-DEscending — the walk order the buttons used to follow. */
function faceNeighbours(d: Sections | null, code: string): Sec[] {
  if (!d) return [];
  const p = parseLocation(code);
  const num = (x: Sec) => x.number ?? parseLocation(x.code).number ?? 0;
  if (p.kind === 'section' && p.letter) {
    const face = d.aisles.flatMap(a => a.faces).find(f => f.letter === p.letter);
    const list = face ? face.sections : [...d.special, ...d.not_in_plan].filter(x => (x.letter ?? parseLocation(x.code).letter) === p.letter);
    return [...list].sort((a, b) => num(a) - num(b));
  }
  return p.kind === 'special' ? d.special : d.not_in_plan;
}

/** The counter's language: the person chosen on a tablet, else the login's own (Settings → My profile). */
function useLang(person: string | null) {
  const [people, setPeople] = useState<Person[]>([]);
  useEffect(() => { jget(`${API}/pp/people`).then(d => setPeople(d.people || [])).catch(() => {}); }, []);
  const who = person || me();
  return people.find(p => p.username === who)?.lang || 'en';
}

// ── sub-navigation ────────────────────────────────────────────────────────────
function Nav({ active }: { active: 'dashboard' | 'work-area' | 'statistics' | 'manager' }) {
  const navigate = useNavigate();
  const items: [string, string][] = [['dashboard', 'Dashboard'], ['work-area', 'Work area']];
  if (elevated()) { items.push(['statistics', 'Statistics']); items.push(['manager', 'Manager']); }
  return (
    <div className="flex gap-1 mb-5 border-b border-gray-800 pb-3">
      {items.map(([key, label]) => (
        <button key={key} onClick={() => navigate(`/tools/wgr-inventory/${key}`)}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            active === key ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'}`}>{label}</button>
      ))}
    </div>
  );
}

// ── the introduction (Fernando, 6.10: "a quick tool introduction that we can put on this tools page") ──
function Intro() {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('wms_inv_intro') !== 'closed'; } catch { return true; } });
  const toggle = () => { const n = !open; setOpen(n); try { localStorage.setItem('wms_inv_intro', n ? 'open' : 'closed'); } catch { /* fine */ } };
  const H = ({ children }: { children: React.ReactNode }) => <h3 className="text-sm font-semibold text-white mt-4 mb-1">{children}</h3>;
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl px-5 py-4 max-w-3xl mb-6 text-sm text-gray-300 leading-relaxed">
      <button onClick={toggle} className="w-full flex items-center justify-between text-left">
        <span className="text-white font-semibold">Inventory — counting the hall, one section at a time</span>
        <span className="text-gray-500 text-xs">{open ? 'hide ▲' : 'how it works ▼'}</span>
      </button>
      {open && (
        <div>
          <p className="mt-2">The shops (Motley's WGR, Nora's Shopify) say how many pieces of each garment we have. This tool checks that against the shelves, section by section, so the numbers customers see are the numbers on the shelf.</p>
          <H>How a count works</H>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open <b className="text-white">Work area</b>. The tool loads the <b className="text-white">hall map</b> once (every barcode and its shelf) — press <i>Load</i> to refresh it. Pick a section — the aisles are listed as you walk them, left and right side.</li>
            <li>Press <b className="text-white">Start counting</b>. The tool takes a fresh snapshot of what <i>should</i> be on that shelf: the shop's stock plus the pieces of orders that have been placed but not picked yet (they are still on the shelf). A picker who reaches a shelf being counted leaves the piece — inventory brings it to the packer.</li>
            <li>Scan every piece in the section. The popup is decided on your laptop at once: green = the count matches, blue = more than expected, red/purple = fewer or none, yellow = this piece belongs on another shelf (the shelf is shown), grey = infinite stock in WGR or a restock shelf — just counted, teal = skipped on purpose. Scans are saved in the background. Products are grouped, with a gap between them; a size that also has restock shelves carries a <b className="text-fuchsia-300">RESTOCK</b> tag (skip those sizes for now — they are counted later, home and restock shelf together). Every row has its buttons: <i>Move</i>, <i>Skip</i> and <i>Clear</i>; a yellow row has <i>Move here</i> and <i>Move home</i>. A piece without a barcode is counted with ±.</li>
            <li>A break: <b className="text-white">Pause</b> stops the counting time (a scan or <i>Continue</i> goes on). A section is never left for the next day — the Work area and the Dashboard show counts still open, red after 17:00. <b className="text-white">Cancel count</b> undoes everything the count changed; the section is as it was before Start.</li>
            <li><b className="text-white">Ready to finish</b> shows the conclusion and the <b className="text-white">Shop</b> block: what will be written to the shop, what waits and why, what was skipped. <b className="text-white">Save count</b> saves, finishes the section and writes. Afterwards: ‹ Prev · Next › · Work area. <i>Reopen</i> is always possible.</li>
          </ol>
          <H>Moving items while arranging</H>
          <p><i>Move</i> on the row, then step ◀ ▶ through the sections of the same letter or type another code. The bar says what happens there: a section <b className="text-white">already counted</b> gets the scanned pieces added when you save; a section <b className="text-white">not counted yet</b> counts them itself — the sizes can be moved before scanning (anything scanned of them here is cleared), the item's shelf changes at once and ✕ undoes it while you count; a section being counted is left alone. Moving only some sizes of a product asks you to confirm.</p>
          <H>What is written to the shops</H>
          <p>Every item is checked across the whole round: all the places it may be — its home, its restock shelves, wherever it was found. When all of them are counted (and nothing else is wrong), the difference is written to its shop at Save count — only the difference, never the whole number. Otherwise the item waits and the conclusion says why: a place not counted yet, a place being counted, a skipped size, the shop not read live, stock that moved during the count with nothing explaining it, or stock that changed between the counts of two shelves (recount one). While writes are switched OFF (Manager → Settings), Save only shows what would be written.</p>
          <H>Misplaced pieces</H>
          <p>A yellow piece counts where it was found. <i>Move here</i> when the item lives on this section now (its shelf changes when you save — refused when its old section already counted pieces of it), <i>Move home</i> when you carry the piece back. Whatever is left can be decided on the Manager page: <i>Move home</i>, <i>Found here</i> or <i>Restock shelf</i>.</p>
          <H>Updates</H>
          <p>A new version of the WMS never interrupts a count. While an update is waiting, <i>Start counting</i> says “Update in progress — check back in about N minutes”; a section already being counted goes on and is saved as usual. Afterwards the page loads the new version by itself before the next section.</p>
          <H>Not in the floor plan</H>
          <p>Stock whose shelf code the floor plan doesn't know yet (the hanger at D 36 / F 36 / H 36, for example). Count it like any section.</p>
          <H>Rounds</H>
          <p>One count of the hall is a round. Sections are counted once per round; a manager can <b className="text-white">Reset</b> a section to recount it, and an admin closes the round when the hall is done. Nothing is deleted — every round stays readable.</p>
        </div>
      )}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
const fmtH = (h: number | null | undefined) => h == null ? '—' : h >= 10 ? `${Math.round(h)} h` : `${h.toFixed(1)} h`;
const fmtClock = (iso: string | null) => { if (!iso) return ''; const d = new Date(iso); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

export function WgrDashboard() {
  const [d, setD] = useState<any | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    const pull = () => jget(`${INV}/dashboard`).then(x => { if (alive) { setD(x); setErr(''); } }).catch(e => { if (alive) setErr(e.message); });
    pull();
    const t = setInterval(pull, 60000);                 // the speed and the people counting now, refreshed by themselves
    return () => { alive = false; clearInterval(t); };
  }, []);
  const Card = ({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: string }) => (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
      <div className="text-gray-500 text-[11px] uppercase tracking-wider mb-1.5">{label}</div>
      <div className={`text-2xl font-bold ${tone || 'text-white'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
  const sp = d?.speed, td = d?.speed?.today;
  const dots = (c: Record<string, number>) => (
    <span className="inline-flex gap-1 items-center">
      {(['green', 'blue', 'red', 'purple', 'yellow', 'skipped'] as const).filter(k => c[k] > 0).map(k => <span key={k} className={`inline-flex items-center gap-0.5 text-[11px] text-gray-400`}><span className={`w-2.5 h-2.5 rounded-sm ${DOT[k]}`} />{c[k]}</span>)}
    </span>
  );
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-1">Inventory</h1>
      <p className="text-gray-500 text-sm mb-5">Stock count of the hall — round {d?.round_id ?? '…'}{d?.sections?.active ? ` · ${d.sections.active} section${d.sections.active === 1 ? '' : 's'} being counted now` : ''}</p>
      <Nav active="dashboard" />
      {err && <div className="text-red-400 text-sm mb-3">{err}</div>}
      {d && (
        <div className="max-w-5xl space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Sections left" value={d.sections.remaining} sub={`${d.sections.done} done of ${d.sections.with_stock} with stock · today ${d.sections.done_today}`} />
            <Card label="Items left" value={d.items.left.toLocaleString()} sub={`${d.items.counted.toLocaleString()} counted of ${d.items.to_count.toLocaleString()}`} />
            <Card label="Hours left — round pace" value={fmtH(sp.hours_left_items)} sub={`by items · by sections ${fmtH(sp.hours_left_sections)}`} tone="text-amber-300" />
            <Card label="Hours left — today's pace" value={fmtH(td.hours_left_items)} sub={`by items · by sections ${fmtH(td.hours_left_sections)}`} tone="text-amber-300" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card label="Sections / hour" value={td.hours_worked > 0.05 ? td.sections_per_hour : sp.sections_per_hour} sub={`today · round ${sp.sections_per_hour}`} />
            <Card label="Items / hour" value={td.hours_worked > 0.05 ? td.items_per_hour : sp.items_per_hour} sub={`today · round ${sp.items_per_hour}`} />
            <Card label="Hours worked" value={fmtH(td.hours_worked)} sub={`today · round ${fmtH(sp.hours_worked)}`} />
            <Card label="Counting now" value={d.sections.active} sub={d.per_person.filter((p: any) => p.counting_now).map((p: any) => p.person).join(', ') || 'nobody'} />
          </div>
          {(d.open_sessions || []).length > 0 && (
            <div className={`rounded-xl border px-4 py-3 text-sm ${d.open_sessions.some((o: any) => o.late) ? 'bg-red-950/50 border-red-700 text-red-200' : 'bg-blue-950/30 border-blue-800 text-blue-200'}`}>
              <b>{d.open_sessions.some((o: any) => o.late) ? 'Still open after 17:00 — save or cancel before leaving' : 'Being counted now'}:</b>{' '}
              {d.open_sessions.map((o: any) => <span key={o.id} className="mr-4">{o.display} · {o.person} · {Math.round(o.minutes)} min{o.paused ? ' · paused' : ''}</span>)}
            </div>
          )}
          {d.per_person.length > 0 && (
            <div className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Person</th><th className="px-3 py-2 text-right">Today h</th><th className="px-3 py-2 text-right">Today scans</th><th className="px-3 py-2 text-right">Today sections</th>
                  <th className="px-3 py-2 text-right">Items / h</th><th className="px-3 py-2 text-right">Round h</th><th className="px-3 py-2 text-right">Round scans</th><th className="px-3 py-2 text-right">Round sections</th><th className="px-3 py-2 text-left">Pace</th>
                </tr></thead>
                <tbody>
                  {d.per_person.map((p: any) => {
                    const best = Math.max(1, ...d.per_person.map((x: any) => x.items_per_hour || 0));
                    return (
                      <tr key={p.person} className="border-b border-gray-800/60">
                        <td className="px-3 py-2 text-white font-medium">{p.person}{p.counting_now && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${p.paused_now ? 'bg-amber-900/60 text-amber-200' : 'bg-blue-900/60 text-blue-200'}`}>{p.paused_now ? 'paused' : 'counting'}</span>}</td>
                        <td className="px-3 py-2 text-right text-gray-300">{fmtH(p.today.hours)}</td><td className="px-3 py-2 text-right text-gray-300">{p.today.scans}</td><td className="px-3 py-2 text-right text-gray-300">{p.today.sections_done}</td>
                        <td className="px-3 py-2 text-right text-white font-semibold">{p.today.hours > 0.05 ? p.today.items_per_hour : p.items_per_hour}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{fmtH(p.hours)}</td><td className="px-3 py-2 text-right text-gray-500">{p.scans}</td><td className="px-3 py-2 text-right text-gray-500">{p.sections_done}</td>
                        <td className="px-3 py-2 w-40"><div className="h-2 bg-gray-800 rounded-full overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${Math.min(100, Math.round(100 * (p.items_per_hour || 0) / best))}%` }} /></div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="space-y-1.5">
            {d.per_aisle.map((a: any) => {
              const pct = a.with_stock ? Math.min(100, Math.round(100 * a.done / a.with_stock)) : 0;
              return (
                <div key={a.name} className="flex items-center gap-3 text-sm">
                  <span className="w-12 text-gray-300 font-medium">{a.name}{a.active > 0 && <span className="inline-block w-2 h-2 rounded-full bg-blue-400 ml-1 align-middle" title="being counted" />}</span>
                  <div className="flex-1 h-3 bg-gray-800 rounded-full overflow-hidden"><div className="h-full bg-green-600" style={{ width: `${pct}%` }} /></div>
                  <span className="w-44 text-right text-gray-500 text-xs">{a.done} / {a.with_stock} · {a.pieces_done.toLocaleString()} / {a.pieces.toLocaleString()} pcs</span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-teal-900/60 text-teal-200">skipped {d.issues.skipped_items} items · {d.issues.skipped_expected} pcs</span>
            <span className="px-2 py-1 rounded-full bg-yellow-900/60 text-yellow-200">wrong place {d.issues.wrong_items} · {d.issues.misplaced_open} open</span>
            <span className="px-2 py-1 rounded-full bg-red-900/60 text-red-200">unknown scans {d.issues.unknown_scans}</span>
            <span className="px-2 py-1 rounded-full bg-orange-900/60 text-orange-200">corrected by the server {d.issues.corrections}</span>
            <span className="px-2 py-1 rounded-full bg-sky-900/60 text-sky-200">moves {d.issues.moves_applied} applied · {d.issues.moves_pending} pending</span>
            {d.per_shop?.map((s: any) => <span key={s.webshop_id} className="px-2 py-1 rounded-full bg-gray-800 text-gray-300">{SHOP[s.platform] || s.platform} {s.counted} pcs</span>)}
          </div>
          {d.today_sections.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-300 mb-2">Finished today ({d.today_sections.length})</h2>
              <div className="bg-[#1a1d27] border border-gray-800 rounded-xl divide-y divide-gray-800/60">
                {d.today_sections.map((t: any) => (
                  <div key={t.code} className="px-3 py-2 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="text-white font-semibold w-16">{t.display}</span>
                    <span className="text-gray-400 w-28 truncate">{t.person}</span>
                    <span className="text-gray-500 w-12">{fmtClock(t.finished_at)}</span>
                    <span className="text-gray-300 w-24">{t.counted ?? '—'} / {t.expected ?? '—'}</span>
                    {dots(t.colors)}
                    {t.skipped > 0 && <span className="text-teal-300 text-xs">{t.skipped} skipped</span>}
                    {t.wrong > 0 && <span className="text-yellow-300 text-xs">{t.wrong} wrong</span>}
                    {t.moves > 0 && <span className="text-sky-300 text-xs">{t.moves} move{t.moves === 1 ? '' : 's'}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <Intro />
        </div>
      )}
      {!d && !err && <Intro />}
    </div>
  );
}

// ── Work area: aisles → faces → sections ──────────────────────────────────────
function SecButton({ s, go }: { s: Sec; go: (code: string) => void }) {
  const cls = s.done ? 'bg-green-900/50 border-green-700 text-green-200'
    : s.active && s.paused ? 'bg-amber-900/50 border-amber-600 text-amber-200'
    : s.active ? 'bg-blue-900/50 border-blue-600 text-blue-200'
    : s.pieces > 0 ? 'bg-gray-800 border-gray-700 text-white hover:border-blue-500'
    : 'bg-gray-900 border-gray-800 text-gray-500';
  return (
    <button onClick={() => go(s.code)} title={`${s.pieces} pcs${s.alias ? ` · also ${s.alias}` : ''}${s.reserved ? ' · reserved hole' : ''}${s.floor_space ? ' · floor space' : ''}`}
      className={`rounded-lg border px-2 py-2.5 text-sm font-semibold leading-tight ${cls}`}>
      <div>{s.display}{s.alias && s.alias !== s.display ? <span className="text-[10px] font-normal text-gray-400"> /{s.alias}</span> : null}</div>
      <div className="text-[10px] font-normal opacity-80">{s.done ? `✓ ${fmtDate(s.last_checked_at)}` : s.active ? (s.paused ? 'paused' : 'counting…') : `${s.pieces} pcs`}</div>
    </button>
  );
}

export function WgrWorkArea() {
  const navigate = useNavigate();
  const [data, setData] = useState<Sections | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [gate, setGate] = useState<Gate | null>(null);
  const wtx = inventoryText(useLang(null));
  useEffect(() => { jget(`${INV}/sections`).then((d: Sections) => { setData(d); setGate(d.update_gate || null); }).catch(e => setErr(e.message)); }, []);
  // the gate can switch on or off while this page is open
  useEffect(() => { const t = window.setInterval(() => { jget(`${INV}/update-gate`).then(setGate).catch(() => {}); }, 60_000); return () => window.clearInterval(t); }, []);
  const go = (code: string) => navigate(`/tools/wgr-inventory/work-area/${encodeURIComponent(code)}`);
  const toggle = (n: string) => { const s = new Set(open); s.has(n) ? s.delete(n) : s.add(n); setOpen(s); };
  const all: Sec[] = data ? [...data.aisles.flatMap(a => a.faces.flatMap(f => f.sections)), ...data.special, ...data.not_in_plan] : [];
  const search = normLocation(q);
  const matches = search ? all.filter(s => s.code.includes(search) || (s.alias && normLocation(s.alias).includes(search))).slice(0, 40) : [];
  // Enter opens the EXACT code when there is one (R 7, not R 70)
  const first = matches.find(s => s.code === search || (s.alias && normLocation(s.alias) === search)) || matches[0];
  // counts still open (Fernando, 9.10: a section is never left for the next day) — red after 17:00
  const openNow = all.filter(s => s.active);
  const late = new Date().getHours() >= 17;
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-1">Inventory</h1>
      <p className="text-gray-500 text-sm mb-5">Pick a section to count — the aisles as the floor plan walks them</p>
      <Nav active="work-area" />
      <div className="mb-4"><CatalogueChip tx={wtx} /></div>
      {gate?.on && <div className="mb-4 max-w-3xl px-4 py-3 rounded-xl bg-amber-950/50 border border-amber-700 text-amber-200 text-sm font-medium">{wtx.updateInProgress(gate.minutes_left)}</div>}
      {openNow.length > 0 && (
        <div className={`mb-4 max-w-3xl px-4 py-3 rounded-xl border text-sm ${late ? 'bg-red-950/50 border-red-700 text-red-200' : 'bg-blue-950/30 border-blue-800 text-blue-200'}`}>
          <b>{late ? wtx.openLate : wtx.openCounts}:</b>{' '}
          {openNow.map(s => <button key={s.code} onClick={() => go(s.code)} className="underline mr-3">{s.display}{s.counted_by ? ` · ${s.counted_by}` : ''}{s.paused ? ` · ${wtx.pausedChip}` : ''}</button>)}
        </div>
      )}
      {err && <div className="text-red-400 text-sm mb-3">{err}</div>}
      <div className="mb-5 max-w-md">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search a section, e.g. R 7" autoFocus
          className="w-full bg-gray-800 border border-gray-700 focus:border-blue-500 text-white rounded-xl px-4 py-2.5 focus:outline-none"
          onKeyDown={e => { if (e.key === 'Enter' && first) go(first.code); }} />
        {search && (
          <div className="mt-2 bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
            {matches.length === 0 && <div className="px-4 py-3 text-gray-500 text-sm">No section matches “{q}”</div>}
            {matches.map(s => (
              <button key={s.code} onClick={() => go(s.code)} className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-800 text-sm">
                <span className="text-white font-medium">{s.display}{s.alias && s.alias !== s.display ? <span className="text-gray-500"> / {s.alias}</span> : null}</span>
                <span className="text-gray-500 text-xs">{s.done ? <span className="text-green-400">✓ {fmtDate(s.last_checked_at)}</span> : `${s.pieces} pcs`}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {data && (
        <div className="space-y-3 max-w-5xl">
          {data.aisles.map(a => {
            const withStock = a.faces.reduce((n, f) => n + f.with_stock, 0), done = a.faces.reduce((n, f) => n + f.done, 0);
            const isOpen = open.has(a.name);
            return (
              <div key={a.name} className="bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
                <button onClick={() => toggle(a.name)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-800/60">
                  <span className="flex items-center gap-3">
                    <span className="text-white font-semibold text-lg">{a.name}</span>
                    <span className="text-gray-500 text-xs">{a.faces.map(f => f.letter).join(' · ')} · {withStock} with stock</span>
                  </span>
                  <span className="flex items-center gap-3 text-xs">
                    {withStock > 0 && done >= withStock ? <span className="text-green-400">✓ all done</span> : <span className="text-gray-400">{done} / {withStock}</span>}
                    <span className="text-gray-600">{isOpen ? '▲' : '▼'}</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-800 p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {a.faces.map(f => (
                      <div key={f.side}>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">{f.side} · face {f.letter}</div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 keep-cols">
                          {f.sections.map(s => <SecButton key={s.code} s={s} go={go} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {(data.special.length > 0 || data.not_in_plan.length > 0) && (
            <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.special.length > 0 && <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Special places</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 keep-cols">{data.special.map(s => <SecButton key={s.code} s={s} go={go} />)}</div>
              </div>}
              {data.not_in_plan.length > 0 && <div>
                <div className="text-xs text-amber-400 uppercase tracking-wider mb-2">Not in the floor plan — stock says it is here</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 keep-cols">{data.not_in_plan.map(s => <SecButton key={s.code} s={s} go={go} />)}</div>
              </div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section rows (top-level components: a re-render must not remount 1,200 rows) ────────────
/** ± never takes the focus: a scanner's Enter would click the button again (review 6.10). */
const noFocus = { tabIndex: -1, onMouseDown: (e: React.MouseEvent) => e.preventDefault() };

function Qty({ r, canEdit, onAdjust }: { r: Row; canEdit: boolean; onAdjust: (r: Row, delta: number) => void }) {
  return (
    <span className="inline-flex items-center gap-1">
      {canEdit && <button {...noFocus} onClick={() => onAdjust(r, -1)} className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-lg leading-none">−</button>}
      <span className="min-w-[2.5rem] text-center font-semibold text-white text-lg">{r.counted_total}</span>
      {canEdit && <button {...noFocus} onClick={() => onAdjust(r, +1)} className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-lg leading-none">+</button>}
    </span>
  );
}

function Chips({ r, tx }: { r: Row; tx: Tx }) {
  return (
    <span className="inline-flex flex-wrap gap-1 ml-1 align-middle">
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.platform === 'shopify' ? 'bg-pink-900/60 text-pink-200' : 'bg-indigo-900/60 text-indigo-200'}`}>{SHOP[r.platform]}</span>
      {r.stock_source === 'sync' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-900/60 text-amber-200" title={tx.syncHint}>{tx.syncChip}</span>}
      {r.in_flight_qty > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-900/60 text-orange-200">{r.in_flight_qty} picking</span>}
      {r.overflow_qty > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-700 text-gray-200">+{r.overflow_qty} on restock shelves</span>}
      {(r.restock?.length || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-900/70 text-fuchsia-100 font-semibold" title={r.restock!.join(', ')}>{tx.restockTag(r.restock!.join(', '))}</span>}
      {r.skipped && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-900/60 text-teal-200">{tx.skippedBy(r.skipped_by || '?')}</span>}
      {(r.moved_in_qty || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-900/60 text-sky-200">+{r.moved_in_qty} moved in from {r.moved_in_from}</span>}
      {(r.moved_out_qty || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-900/60 text-sky-200">moved → {r.moved_to}</span>}
      {!r.barcode && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-900/60 text-red-200">no barcode — use ±</span>}
    </span>
  );
}

/** Skip / Clear for the counter (Fernando, 8.10: "the clear buttons are for the worker scanning the items"). One
 *  product = every row of the section with the same label; the menu offers this size or all sizes. */
export type RowAction = 'skip_size' | 'skip_all' | 'unskip' | 'clear_all' | 'move' | 'here' | 'home';
interface RowMenu {
  siblingsOf: (r: Row) => number; openId: number | null; onToggle: (id: number) => void; onAction: (a: RowAction, r: Row) => void;
  moving: Map<number, Move>; selected: Set<number>; sectionName: string; onCancelMove: (id: number) => void;
}
const BTN = 'px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed';

/** The row's buttons while counting (Fernando, 8.10: visible on every row, never a hidden menu, fitting the row).
 *  Home rows: Move (the move bar), Skip (with more sizes: this size / all sizes), Clear (all sizes of the product).
 *  Wrong-place rows: Move here (the item lives on this section from Save count), Move home, Clear. */
function RowActions({ r, tx, menu }: { r: Row; tx: Tx; menu: RowMenu }) {
  const siblings = menu.siblingsOf(r);
  const mv = menu.moving.get(r.wms_id);
  const counted = r.counted_total > 0;
  const act = (a: RowAction) => (e: React.MouseEvent) => { e.stopPropagation(); menu.onAction(a, r); };
  const chip = mv && (
    <button {...noFocus} onClick={e => { e.stopPropagation(); menu.onCancelMove(mv.id); }} title={tx.cancelMove}
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-900/70 text-sky-200 hover:bg-red-900/60 whitespace-nowrap">
      {mv.direction === 'here' ? `↓ ${tx.hereChip}` : `→ ${mv.to}${mv.kind === 'unscanned' ? ` (${tx.notScanned})` : ''}`} ✕
    </button>
  );
  if (r.role === 'home' && menu.openId === r.wms_id && !r.skipped && siblings > 1) {
    // Skip with more sizes: the two choices in place of the buttons, inside the row (never clipped)
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        <button {...noFocus} onClick={act('skip_size')} className={`${BTN} bg-teal-800 text-teal-50 hover:bg-teal-700`}>{tx.skipThisSize(r.size || '')}</button>
        <button {...noFocus} onClick={act('skip_all')} className={`${BTN} bg-teal-800 text-teal-50 hover:bg-teal-700`}>{tx.skipAllSizes(siblings)}</button>
        <button {...noFocus} onClick={e => { e.stopPropagation(); menu.onToggle(r.wms_id); }} className={`${BTN} bg-gray-800 text-gray-300 hover:bg-gray-700`}>✕</button>
      </span>
    );
  }
  if (r.role === 'home') {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        {chip}
        <button {...noFocus} onClick={act('move')}
                className={`${BTN} ${menu.selected.has(r.wms_id) ? 'bg-sky-500 text-white' : 'bg-sky-900/70 text-sky-100 hover:bg-sky-800'}`}>{tx.move}</button>
        {r.skipped
          ? <button {...noFocus} onClick={act('unskip')} className={`${BTN} bg-teal-900/70 text-teal-100 hover:bg-teal-800`}>{tx.unskip}</button>
          : <button {...noFocus} onClick={e => { e.stopPropagation(); if (siblings > 1) menu.onToggle(r.wms_id); else menu.onAction('skip_size', r); }}
                    className={`${BTN} bg-gray-800 text-gray-200 hover:bg-gray-700`}>{tx.skip}{siblings > 1 ? ' ▾' : ''}</button>}
        <button {...noFocus} onClick={act('clear_all')} className={`${BTN} bg-gray-800 text-red-300 hover:bg-red-950/60`}>{tx.clear}</button>
      </span>
    );
  }
  if (r.role === 'misplaced') {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-1">
        {chip}
        <button {...noFocus} onClick={act('here')} disabled={!counted || mv?.direction === 'here'} title={tx.moveHereHint(menu.sectionName)}
                className={`${BTN} bg-yellow-600/90 text-black hover:bg-yellow-500`}>{tx.moveHere}</button>
        <button {...noFocus} onClick={act('home')} disabled={!counted || !r.home_code || (!!mv && mv.direction === 'out')}
                title={r.home_code ? tx.moveHomeHint(r.home || r.home_code) : tx.noHomeToGo}
                className={`${BTN} bg-sky-900/70 text-sky-100 hover:bg-sky-800`}>{tx.moveHome}</button>
        <button {...noFocus} onClick={act('clear_all')} className={`${BTN} bg-gray-800 text-red-300 hover:bg-red-950/60`}>{tx.clear}</button>
      </span>
    );
  }
  return null;
}

interface RowProps {
  r: Row; tx: Tx; canEdit: boolean; onAdjust: (r: Row, delta: number) => void; homeCol?: 'home' | 'found'; idPrefix: string;
  menu?: RowMenu;
}
const hasActions = (r: Row, canEdit: boolean, menu?: RowMenu): menu is RowMenu => !!menu && canEdit && (r.role === 'home' || r.role === 'misplaced');

function RowCard({ r, tx, canEdit, onAdjust, homeCol, idPrefix, menu }: RowProps) {
  const extra = homeCol ? <div className="text-xs text-yellow-300">{homeCol === 'home' ? (r.role === 'overflow' ? tx.countsToward(r.home || '?') : `→ ${r.home || '—'}`) : `found at ${r.found_at}`}</div> : null;
  return (
    <div id={`${idPrefix}-${r.wms_id}`} className={`border-l-4 pl-3 ${ROW_CLS[r.color] || ROW_CLS.white}`}>
      <div className="flex justify-between gap-2">
        <div className="min-w-0">
          <div className="text-white font-semibold text-lg leading-tight truncate">{r.label}{r.size ? <span className="text-gray-400 font-normal"> · {r.size}</span> : null}</div>
          <div className="text-xs text-gray-500 truncate">{r.name || r.article_number}<Chips r={r} tx={tx} /></div>
          {extra}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] text-gray-500 uppercase">{tx.expected}</div>
          <div className="text-white font-semibold">{r.infinite ? '∞' : r.role === 'home' ? r.expected_qty : '—'}</div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-[10px] text-gray-500 uppercase">{tx.counted}</span>
        <Qty r={r} canEdit={canEdit && homeCol !== 'found'} onAdjust={onAdjust} />
      </div>
      {hasActions(r, canEdit, menu) && <div className="mt-1.5 flex justify-end"><RowActions r={r} tx={tx} menu={menu} /></div>}
    </div>
  );
}

/** The product a size belongs to: master_sku (Motley 159113-360 for every size, Nora mari-purple). The picklist number
 *  carries the size (159113-360-10XL) — grouping by it made every size a product of its own (Fernando, 10.10). */
const productKey = (r: Row) => `${r.platform}:${r.master_sku || r.picklist_number || r.label}`;

/** The rows of one product together, in the order the products first appear (a size scanned later joins its product). */
function groupRows(list: Row[]): Row[][] {
  const order: string[] = [];
  const by = new Map<string, Row[]>();
  for (const r of list) {
    const k = productKey(r);
    if (!by.has(k)) { by.set(k, []); order.push(k); }
    by.get(k)!.push(r);
  }
  return order.map(k => by.get(k)!);
}
const rowKey = (r: Row) => `${r.wms_id}-${r.found_at || ''}`;

function RowTable({ list, tx, canEdit, onAdjust, homeCol, idPrefix, menu }: Omit<RowProps, 'r'> & { list: Row[] }) {
  const phone = useIsPhone();
  const groups = useMemo(() => groupRows(list), [list]);
  const withActions = !!menu && canEdit && list.some(r => r.role === 'home' || r.role === 'misplaced');
  const cols = 5 + (homeCol ? 1 : 0) + (withActions ? 1 : 0);
  if (phone) {
    return (
      <div className="space-y-5">
        {list.length === 0 && <div className="text-center py-6 text-gray-500 text-sm">Nothing here</div>}
        {groups.map(g => (
          <div key={rowKey(g[0])} className="space-y-1.5">
            {g.map(r => <div key={rowKey(r)} className="rounded-xl border border-gray-800 bg-[#1a1d27] px-3 py-2.5 text-sm">
              <RowCard r={r} tx={tx} canEdit={canEdit} onAdjust={onAdjust} homeCol={homeCol} idPrefix={idPrefix} menu={menu} /></div>)}
          </div>
        ))}
      </div>
    );
  }
  return (
    <PhoneCards rows={list} keyOf={rowKey}
      card={r => <RowCard r={r} tx={tx} canEdit={canEdit} onAdjust={onAdjust} homeCol={homeCol} idPrefix={idPrefix} menu={menu} />}>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
            <th className="px-3 py-2 text-left">Item</th><th className="px-2 py-2 text-left">Size</th><th className="px-3 py-2 text-left hidden xl:table-cell">Barcode</th>
            {homeCol && <th className="px-2 py-2 text-left">{homeCol === 'home' ? 'Home' : 'Found at'}</th>}
            <th className="px-2 py-2 text-center">{tx.expected}</th><th className="px-2 py-2 text-center">{tx.counted}</th>
            {withActions && <th className="px-2 py-2" />}
          </tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={cols} className="text-center py-8 text-gray-500">Nothing here</td></tr>}
            {groups.map((g, gi) => <React.Fragment key={rowKey(g[0])}>
              {gi > 0 && <tr aria-hidden><td colSpan={cols} className="h-3 p-0 bg-[#0f1117]" /></tr>}
              {g.map(r => (
              <tr key={rowKey(r)} id={`${idPrefix}-${r.wms_id}`} className={`border-b border-gray-800/70 border-l-4 ${ROW_CLS[r.color] || ROW_CLS.white}`}>
                <td className="px-3 py-2 min-w-[10rem]">
                  <span className="text-white font-medium">{r.label}</span><Chips r={r} tx={tx} />
                  <div className="text-[11px] text-gray-500">{r.name || r.article_number}</div>
                  <div className="xl:hidden text-[10px] text-gray-600 font-mono">{r.barcode || <span className="text-red-400">no barcode</span>}</div>
                </td>
                <td className="px-2 py-2 text-gray-400">{r.size || ''}</td>
                <td className="px-3 py-2 text-gray-500 font-mono text-xs hidden xl:table-cell">{r.barcode || <span className="text-red-400">none</span>}</td>
                {homeCol && <td className="px-2 py-2 text-yellow-300 font-semibold whitespace-nowrap">{homeCol === 'home' ? (r.home || '—') : r.found_at}{r.fixed && <span className="ml-1.5 text-[10px] bg-green-900/60 text-green-300 px-1.5 py-0.5 rounded-full">{r.resolution || 'fixed'}</span>}</td>}
                <td className="px-2 py-2 text-center text-white font-semibold" title={r.infinite ? 'infinite stock (WGR) — no target, just count' : r.role === 'home' ? `live ${r.live_stock ?? '?'} + unpicked ${r.unpicked_qty}` : ''}>{r.infinite ? '∞' : r.role === 'home' ? r.expected_qty : '—'}</td>
                <td className="px-2 py-2 text-center whitespace-nowrap">
                  {homeCol === 'found' ? <span className="font-semibold text-white">{r.counted_total}</span> : <Qty r={r} canEdit={canEdit} onAdjust={onAdjust} />}
                  <div className="text-[10px] text-gray-500" title="saved earlier · scanned in this count">saved {r.scanned_saved_qty} · now {r.ongoing_qty}</div>
                </td>
                {withActions && <td className="px-2 py-2 text-right">{hasActions(r, canEdit, menu) && <RowActions r={r} tx={tx} menu={menu} />}</td>}
              </tr>
              ))}
            </React.Fragment>)}
          </tbody>
        </table>
      </div>
    </PhoneCards>
  );
}

// ── Section: the count ────────────────────────────────────────────────────────
interface Summary {
  code: string; display: string; snapshot_at: string | null; saved_at: string | null; done: boolean;
  session: { id: number; person: string; device: string | null; status: string; started_at: string | null; last_scan_at: string | null;
             duration_s: number | null; scans: number; corrections: number; unknown: number; paused_at?: string | null } | null;
  totals: { expected: number; counted: number; scanned: number; typed: number; skipped_lines: number; skipped_expected: number; extra: number;
            missing: number; wrong_place: number; nos_counted: number; overflow_counted: number; moved_in: number; moved_out: number; lines: number;
            moved_unscanned?: number };
  shop?: Shop; preview?: boolean;
  rows: { missing: any[]; extra: any[]; skipped: any[]; wrong_place: any[]; found_elsewhere: any[] };
  moves: { pending: any[]; applied: any[] };
  movement: { ok_lines: number; unexplained_lines: number; lines: any[] };
  warnings: string[];
}

function fmtDur(s: number | null | undefined): string {
  if (s == null) return '—';
  const m = Math.floor(s / 60), h = Math.floor(m / 60);
  return h ? `${h} h ${m % 60} min` : `${m} min ${s % 60} s`;
}

/** The Shop block of the conclusion (9.10 — no manager step, so what is written is seen before Save): per item, decided
 *  across the round — Will be written / Held (why) / Skipped; after Save what was sent and what came back. */
function ShopBlock({ shop, tx }: { shop: Shop; tx: Tx }) {
  const T = tx.shop;
  const holdTxt = (r: ShopRow) => r.holds.map(h => (tx.holdText[h.code] ? tx.holdText[h.code](h.places.join(', ')) : h.text)).join('; ');
  const where = (r: ShopRow) => r.places.filter(p => p.counted > 0).map(p => `${p.display} ${p.counted}`).join(' · ');
  const Part = ({ title, rows, tone }: { title: string; rows: ShopRow[]; tone: string }) => rows.length === 0 ? null : (
    <div className="mt-3">
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-1 ${tone}`}>{title} ({rows.length})</h4>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl divide-y divide-gray-800/70">
        {rows.map(r => (
          <div key={r.wms_id} className="px-3 py-2 text-sm flex justify-between gap-3">
            <span className="min-w-0">
              <span className="text-white font-medium">{r.label}{r.size ? <span className="text-gray-400 font-normal"> · {r.size}</span> : null}</span>
              <div className="text-xs text-gray-500">{T.counted} {r.counted ?? '—'}{where(r) ? ` (${where(r)})` : ''}{r.expected != null ? ` · ${T.expected} ${r.expected}` : ''}{r.written ? ` · ${T.already} ${sign(r.written)}` : ''}</div>
              {r.holds.length > 0 && <div className="text-xs text-amber-300">{holdTxt(r)}</div>}
            </span>
            <span className={`shrink-0 font-bold ${tone}`}>{r.delta ? sign(r.delta) : r.pending ? sign(r.pending) : r.status === 'written' ? sign(r.written) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-white">{T.title}{shop.write.length > 0 && <span className="ml-2 text-xs font-normal text-gray-400">+{shop.pieces_plus} / {shop.pieces_minus} pcs</span>}</h3>
      <div className={`mt-1 px-3 py-2 rounded-lg text-xs ${shop.mode === 'on' ? 'bg-green-950/40 border border-green-800 text-green-200' : 'bg-gray-800 text-gray-300'}`}>{shop.mode === 'on' ? T.modeOn : T.modeOff}</div>
      <Part title={T.write} rows={shop.write} tone="text-white" />
      <Part title={T.pending} rows={shop.pending} tone="text-gray-300" />
      <Part title={T.written} rows={shop.written} tone="text-green-300" />
      <Part title={T.review} rows={shop.review} tone="text-red-300" />
      <Part title={T.held} rows={shop.held} tone="text-amber-300" />
      <Part title={T.skipped} rows={shop.skipped} tone="text-teal-300" />
      {(shop.later || 0) > 0 && <p className="text-xs text-gray-500 mt-2">{T.later(shop.later || 0, (shop.later_places || []).join(', '))}</p>}
      {(shop.nothing > 0 || shop.infinite > 0) && <p className="text-xs text-gray-500 mt-2">{T.nothing(shop.nothing)}{shop.infinite ? ` · ${T.infinite(shop.infinite)}` : ''}</p>}
    </div>
  );
}

/** The conclusion screen (Fernando, 8.10): "Ready to finish" shows it for checking; "Save count" saves and finishes.
 *  After Save: ‹ Prev · Next › · Work area (9.10). */
function Conclusion({ sum, saved, busy, tx, onBack, onSave, onNext, onPrev, onWorkArea, nextLabel, prevLabel }: {
  sum: Summary; saved: boolean; busy: boolean; tx: Tx; onBack: () => void; onSave: () => void; onNext: (() => void) | null; onPrev: (() => void) | null;
  onWorkArea: () => void; nextLabel: string; prevLabel: string;
}) {
  const S = tx.summary;
  const t = sum.totals;
  const Tile = ({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) => (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-3">
      <div className="text-gray-500 text-[11px] uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-bold ${tone || 'text-white'}`}>{value}</div>
    </div>
  );
  const List = ({ title, rows, render, tone }: { title: string; rows: any[]; render: (r: any) => React.ReactNode; tone: string }) => rows.length === 0 ? null : (
    <div className="mt-4">
      <h3 className={`text-sm font-semibold mb-1.5 ${tone}`}>{title} ({rows.length})</h3>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl divide-y divide-gray-800/70">
        {rows.map((r, i) => <div key={i} className="px-3 py-2 text-sm flex justify-between gap-3">{render(r)}</div>)}
      </div>
    </div>
  );
  const name = (r: any) => <span className="text-white font-medium truncate">{r.label}{r.size ? <span className="text-gray-400 font-normal"> · {r.size}</span> : null}</span>;
  const clean = !t.missing && !t.extra && !t.wrong_place && !t.skipped_lines && !sum.movement.unexplained_lines && !sum.rows.found_elsewhere.length && !sum.warnings.length;
  return (
    <div className="max-w-3xl">
      <div className={`rounded-xl px-4 py-3 mb-4 border ${saved ? 'bg-green-950/40 border-green-800 text-green-200' : 'bg-blue-950/40 border-blue-800 text-blue-200'}`}>
        <div className="text-lg font-semibold">{saved ? S.saved : S.preview}</div>
        {sum.session && <div className="text-xs opacity-80 mt-0.5">{sum.session.person} · {S.duration} {fmtDur(sum.session.duration_s)} · {sum.session.scans} {S.scanned}{sum.session.corrections ? ` · ${sum.session.corrections} corrected by the server` : ''}</div>}
      </div>
      {sum.warnings.length > 0 && <div className="mb-3 space-y-1">{sum.warnings.map((w, i) => <div key={i} className="px-3 py-2 rounded-lg bg-amber-950/40 border border-amber-800 text-amber-200 text-sm">{w}</div>)}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label={S.expected} value={t.expected} />
        <Tile label={S.counted} value={<span>{t.counted}<span className="text-xs text-gray-500 font-normal ml-2">{t.scanned} {S.scanned}{t.typed ? ` · ${t.typed} ${S.typed}` : ''}</span></span>} />
        <Tile label={S.skipped} value={t.skipped_lines ? `${t.skipped_lines} / ${t.skipped_expected} pcs` : 0} tone={t.skipped_lines ? 'text-teal-300' : undefined} />
        <Tile label={S.missing} value={t.missing} tone={t.missing ? 'text-red-400' : undefined} />
        <Tile label={S.extra} value={t.extra} tone={t.extra ? 'text-blue-300' : undefined} />
        <Tile label={S.wrongPlace} value={t.wrong_place} tone={t.wrong_place ? 'text-yellow-300' : undefined} />
        {(t.moved_in > 0 || t.moved_out > 0 || sum.moves.pending.length > 0) && <Tile label={`${S.movedIn} / ${S.movedOut}`} value={`${t.moved_in} / ${t.moved_out}`} tone="text-sky-300" />}
        {(t.moved_unscanned || 0) > 0 && <Tile label={S.movedUnscanned} value={t.moved_unscanned} tone="text-sky-300" />}
        {t.nos_counted > 0 && <Tile label={S.nos} value={t.nos_counted} tone="text-gray-300" />}
        {t.overflow_counted > 0 && <Tile label={S.restock} value={t.overflow_counted} tone="text-gray-300" />}
      </div>
      {clean && <p className="text-green-300 text-sm mt-4">{S.noIssues}</p>}
      <List title={S.wrongRows} tone="text-yellow-300" rows={sum.rows.wrong_place} render={r => <>{name(r)}<span className="text-gray-300 shrink-0">{r.qty} × → <b className="text-yellow-200">{r.home || tx.noHomeShelf}</b></span></>} />
      <List title={S.missingRows} tone="text-red-300" rows={sum.rows.missing} render={r => <>{name(r)}<span className="text-gray-300 shrink-0">{r.counted_total} / {r.expected_qty} <b className="text-red-300">−{r.short}</b></span></>} />
      <List title={S.extraRows} tone="text-blue-300" rows={sum.rows.extra} render={r => <>{name(r)}<span className="text-gray-300 shrink-0">{r.counted_total} / {r.expected_qty} <b className="text-blue-300">+{r.over}</b></span></>} />
      <List title={S.skippedRows} tone="text-teal-300" rows={sum.rows.skipped} render={r => <>{name(r)}<span className="text-gray-400 shrink-0">{r.expected_qty} pcs · {tx.skippedBy(r.skipped_by || '?')}</span></>} />
      <List title={S.foundRows} tone="text-sky-300" rows={sum.rows.found_elsewhere} render={r => <>{name(r)}<span className="text-gray-300 shrink-0">{r.qty} × at {r.found_at}</span></>} />
      <List title={`${S.moves}${saved ? '' : ` — ${S.movesPending}`}`} tone="text-sky-300" rows={[...sum.moves.pending, ...sum.moves.applied]}
            render={r => <>{name(r)}<span className="text-gray-300 shrink-0">{r.qty} × {r.from} → {r.to}{r.kind === 'unscanned' ? ` (${tx.notScanned})` : ''}{r.status === 'applied' ? ' ✓' : ''}{r.note ? <span className="block text-[11px] text-gray-500">{r.note}</span> : null}</span></>} />
      {sum.movement.lines.length > 0 && (
        <List title={S.movement} tone={sum.movement.unexplained_lines ? 'text-amber-300' : 'text-gray-300'} rows={sum.movement.lines}
              render={r => <>
                <span className="min-w-0">{name(r)}<div className="text-xs text-gray-500">{(r.events || []).map((e: any) => `${e.kind} ${e.ref} ${e.qty > 0 ? '+' : ''}${e.qty}`).join(' · ') || '—'}</div></span>
                <span className={`shrink-0 text-xs ${r.ok ? 'text-green-300' : 'text-amber-300'}`}>{r.moved > 0 ? '+' : ''}{r.moved} · {r.ok ? S.movementOk : S.movementBad}</span>
              </>} />
      )}
      {sum.shop && <ShopBlock shop={sum.shop} tx={tx} />}
      <div className="flex flex-wrap gap-2 mt-6">
        {!saved && <button onClick={onBack} disabled={busy} className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg">{tx.backToCounting}</button>}
        {!saved && <button onClick={onSave} disabled={busy} className="px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">{busy ? '…' : tx.saveCount}</button>}
        {saved && onPrev && <button onClick={onPrev} className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg">{prevLabel}</button>}
        {saved && onNext && <button onClick={onNext} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg">{nextLabel}</button>}
        {saved && <button onClick={onWorkArea} className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg">{tx.workArea}</button>}
      </div>
    </div>
  );
}

/** The target as the server sees it (GET move-target): counted → scanned pieces are added there; not counted → it counts
 *  them (the sizes move before scanning); being counted → left alone. */
interface MoveTarget { code: string; display: string; same: boolean; reserved: boolean; in_plan: boolean; state: 'counted' | 'not_counted' | 'being_counted'; by: string | null }

/** Moving pieces while arranging (Fernando, 8.10 / 9.10): ◀◀ ◀ step the SAME letter in number order ▶ ▶▶, or any other
 *  section; what happens depends on the target — see MoveTarget. */
function MoveBar({ rows, nb, code, sections, tx, busy, onMove, onCancel, onToggleRow }: {
  rows: Row[]; nb: Sec[]; code: string; sections: Sections | null; tx: Tx; busy: boolean;
  onMove: (target: string, wmsIds: number[], t: MoveTarget) => void; onCancel: () => void; onToggleRow: (id: number) => void;
}) {
  const idx = nb.findIndex(x => x.code === code);
  const [ti, setTi] = useState<number>(idx > 0 ? idx - 1 : (idx >= 0 && idx < nb.length - 1 ? idx + 1 : -1));
  const [other, setOther] = useState('');
  const [otherOn, setOtherOn] = useState(false);
  const target = otherOn ? normLocation(other) : (ti >= 0 && ti < nb.length ? nb[ti].code : '');
  const all: Sec[] = sections ? [...sections.aisles.flatMap(a => a.faces.flatMap(f => f.sections)), ...sections.special, ...sections.not_in_plan] : [];
  const tsec = all.find(x => x.code === target);
  const pcs = rows.reduce((n, r) => n + r.scanned_saved_qty + r.ongoing_qty, 0);
  const step = (d: number) => { setOtherOn(false); setTi(t => { let n = (t < 0 ? idx : t) + d; if (n === idx) n += d; return Math.max(0, Math.min(nb.length - 1, n)); }); };
  const [mt, setMt] = useState<MoveTarget | null>(null);
  useEffect(() => {
    setMt(null);
    if (!target || target === code) return;
    let alive = true;
    const t = window.setTimeout(() => {
      jget(`${INV}/sections/${encodeURIComponent(code)}/move-target?to=${encodeURIComponent(target)}`).then((x: MoveTarget) => { if (alive) setMt(x); }).catch(() => {});
    }, 200);
    return () => { alive = false; window.clearTimeout(t); };
  }, [target, code]);
  const tName = mt?.display || tsec?.display || parseLocation(target).display || target;
  const allScanned = rows.every(r => r.counted_total > 0);
  const anyWrongPlace = rows.some(r => r.role === 'misplaced');
  const hint = !mt ? '' : mt.reserved ? tx.targetReserved(tName) : mt.state === 'being_counted' ? tx.targetBusy(tName, mt.by || '?')
    : mt.state === 'counted' ? (allScanned ? tx.targetCounted(tName, pcs) : tx.scanFirst) : (anyWrongPlace ? tx.moveHomeHint(tName) : tx.targetNotCounted(tName));
  const canMove = !!mt && !mt.same && !mt.reserved && mt.state !== 'being_counted' && (mt.state === 'not_counted' ? !anyWrongPlace : allScanned);
  return (
    <div className="mb-4 rounded-xl border border-sky-800 bg-sky-950/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sky-200 font-semibold">{tx.moveBarTitle(rows.length, pcs)}</span>
        <button {...noFocus} onClick={() => step(-5)} disabled={!nb.length} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-30">◀◀</button>
        <button {...noFocus} onClick={() => step(-1)} disabled={!nb.length} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-30">◀</button>
        <span className={`px-3 py-1.5 rounded-lg font-bold min-w-[7rem] text-center ${target && target !== code ? 'bg-sky-700 text-white' : 'bg-gray-800 text-gray-500'}`}>
          {target ? (tsec?.display || parseLocation(target).display || target) : '—'}
          {tsec && <span className="block text-[10px] font-normal opacity-80">{mt?.state === 'counted' ? '✓ ' : mt?.state === 'being_counted' ? '● ' : ''}{tx.holds(tsec.pieces)}</span>}
        </span>
        <button {...noFocus} onClick={() => step(1)} disabled={!nb.length} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-30">▶</button>
        <button {...noFocus} onClick={() => step(5)} disabled={!nb.length} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-30">▶▶</button>
        {otherOn
          ? <input autoFocus value={other} onChange={e => setOther(e.target.value)} placeholder={tx.otherSectionPlaceholder}
                   onKeyDown={e => { if (e.key === 'Enter' && canMove && mt) onMove(target, rows.map(r => r.wms_id), mt); }}
                   className="w-40 bg-gray-900 border border-sky-700 text-white rounded-lg px-3 py-1.5 focus:outline-none font-mono" />
          : <button {...noFocus} onClick={() => setOtherOn(true)} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300">{tx.otherSection}</button>}
        <div className="flex-1" />
        <button {...noFocus} onClick={() => canMove && mt && onMove(target, rows.map(r => r.wms_id), mt)} disabled={busy || !canMove}
                className="px-4 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white font-semibold">{tx.moveNow(tName || '…')}</button>
        <button {...noFocus} onClick={onCancel} className="px-2 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300">✕</button>
      </div>
      {hint && <div className={`mt-2 text-xs ${canMove ? 'text-sky-200' : 'text-amber-300'}`}>{hint}</div>}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {rows.map(r => <button key={r.wms_id} {...noFocus} onClick={() => onToggleRow(r.wms_id)} className="text-xs px-2 py-1 rounded-full bg-sky-900/70 text-sky-100 hover:bg-red-900/60">{r.label}{r.size ? ` ${r.size}` : ''} × {r.counted_total} ✕</button>)}
      </div>
    </div>
  );
}

export function WgrSection() {
  const { stockplace = '' } = useParams<{ stockplace: string }>();
  const navigate = useNavigate();
  const phone = useIsPhone();
  const scanRef = useRef<HTMLInputElement>(null);
  const scanTimer = useRef<number | null>(null);
  const lastKey = useRef<number>(0);
  const typed = useRef(false);                  // a human typed (slow keys): wait for Enter, never the burst timer
  const loadSeq = useRef(0);                    // answers for a section we have left are dropped
  const adjusting = useRef(false);
  const lastScan = useRef<{ ean: string; t: number }>({ ean: '', t: 0 });
  const queueRef = useRef<ScanQueue | null>(null);
  const detailRef = useRef<Detail | null>(null);
  const [detail, setDetailState] = useState<Detail | null>(null);
  const setDetail = (d: Detail | null | ((p: Detail | null) => Detail | null)) => {
    setDetailState(p => { const n = typeof d === 'function' ? (d as any)(p) : d; detailRef.current = n; return n; });
  };
  const [err, setErr] = useState('');
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [person, setPerson] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [sections, setSections] = useState<Sections | null>(null);
  const [flash, setFlash] = useState('');
  const [popup, setPopup] = useState<{ bg: string; fg: string; title: string; big: string; small: string; sticky?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingN, setPendingN] = useState(0);
  const [offline, setOffline] = useState(false);
  const [view, setView] = useState<'count' | 'conclusion'>('count');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [saved, setSaved] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [moveSel, setMoveSel] = useState<Set<number>>(new Set());
  const [notice, setNotice] = useState('');
  const { cat } = useCatalogue();
  const tx = inventoryText(useLang(person));
  const txRef = useRef(tx);
  txRef.current = tx;
  const stale = useBuildStale();
  const code = normLocation(stockplace);        // from the URL, never from a previous section's answer
  const codes = detail?.codes || [code];

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const d: Detail = await jget(`${INV}/sections/${encodeURIComponent(code)}`);
      if (seq !== loadSeq.current) return;        // we moved on
      setDetail(d);
      const mine = !!d.active_session && (d.active_session.person === me() || d.active_session.device === me());
      setErr(d.update_gate?.on && !mine ? txRef.current.updateInProgress(d.update_gate.minutes_left) : '');
      // a session left active by me (or on this tablet) is resumed: the server keeps one per section
      if (d.active_session && (d.active_session.person === me() || d.active_session.device === me())) {
        setSessionId(d.active_session.id); setPerson(d.active_session.person);
      } else {
        setSessionId(null);
      }
    } catch (e: any) { if (seq === loadSeq.current) setErr(e.message); }
  }, [code]);
  // a new section: nothing of the previous one survives
  useEffect(() => {
    setDetail(null); setSessionId(null); setFlash(''); setPopup(null); setErr(''); setView('count'); setSummary(null); setSaved(false); setMenuId(null); setNotice('');
    queueRef.current?.destroy(); queueRef.current = null; setPendingN(0); setOffline(false);
    load();
  }, [code, load]);
  useEffect(() => { jget(`${INV}/sections`).then((d: Sections) => setSections(d)).catch(() => {}); }, []);
  useEffect(() => () => { queueRef.current?.destroy(); }, []);

  const scanning = sessionId !== null;
  const paused = scanning && !!detail?.active_session?.paused_at;
  // (never over a sticky popup: it lists scans that were not recorded)
  useEffect(() => { if (stale && !scanning && view === 'count' && !asking && !busy && !popup?.sticky) reloadFresh(); }, [stale, scanning, view, asking, busy, popup]);
  const nb = faceNeighbours(sections, code);
  const nbIdx = nb.findIndex(x => x.code === code);
  const prevSec = nbIdx > 0 ? nb[nbIdx - 1] : null;
  const nextSec = nbIdx >= 0 && nbIdx < nb.length - 1 ? nb[nbIdx + 1] : null;
  const goTo = (target: string) => navigate(`/tools/wgr-inventory/work-area/${encodeURIComponent(target)}`);

  // ── the queue: the server records in the background and corrects the screen where it decided differently ──
  const onResults = useCallback((results: ScanResult[], lines: LineState[], sent: QueuedScan[]) => {
    const byCid = new Map(sent.map(q => [q.cid, q]));
    let correction: { from: string; to: string } | null = null;
    for (const r of results) {
      const q = byCid.get(r.cid);
      if (!q || r.duplicate) continue;
      const localItem = q.local.wms_id != null ? cat?.byId.get(q.local.wms_id) : null;
      if (!r.agree && q.local.status !== 'pending') {
        const statusTxt = (st: string) => st === 'home' ? tx.correct.replace('✓ ', '') : st === 'overflow' ? tx.overflowPopup : st === 'misplaced' ? tx.wrongPlace : st;
        const from = localItem ? `${localItem.label}${localItem.size ? ' ' + localItem.size : ''} · ${statusTxt(q.local.status)}` : q.local.status === 'unknown_ean' ? tx.unknown : statusTxt(q.local.status);
        const to = r.wms_id ? `${r.label}${r.size ? ' ' + r.size : ''} · ${r.status === 'misplaced' ? `${tx.wrongPlace} → ${r.home || tx.noHomeShelf}` : statusTxt(r.status)}` : r.status === 'ambiguous' ? tx.ambiguous : tx.unknown;
        correction = { from, to };
      }
      // the server knows a barcode the laptop did not (a WGR lookup), or the item's home moved since Load: remember it —
      // merging, so the known shop and stock (the tie-break rule) are kept (review M2)
      const known = cat?.byId.get(r.wms_id || -1);
      if (r.wms_id && r.label !== undefined && (!known || q.local.status === 'unknown_ean' || (r.home_code || '') !== known.home)) {
        patchCatalogue({ wms_id: r.wms_id, ws: known?.ws || '', platform: (r.platform as any) || known?.platform || 'wgr', barcode: q.ean, label: r.label || known?.label || '',
                         size: r.size || known?.size || '', home: r.home_code || '', restock: r.restock ? r.restock.split(',').filter(Boolean) : (known?.restock || []),
                         stock_qty: known?.stock_qty ?? 0, nos: r.infinite ?? known?.nos ?? false, product: known?.product });
      }
    }
    if (correction) {
      setPopup({ ...POPUP.correction, title: tx.corrected(correction.from, correction.to), big: '', small: '' });
      setFlash(tx.corrected(correction.from, correction.to));
      load();                                            // the optimistic row of the wrong decision must not linger (review M3)
      return;
    }
    if (lines.length) {
      const resultById = new Map<number, ScanResult>();
      for (const r of results) if (r.wms_id) resultById.set(r.wms_id, r);
      setDetail(d => {
        if (!d) return d;
        const apply = (list: Row[], role: Row['role']): Row[] => list.map(row => {
          const ln = lines.find(l => l.wms_id === row.wms_id && l.role === role);
          return ln ? { ...row, scanned_saved_qty: ln.scanned_saved_qty, ongoing_qty: ln.ongoing_qty, overflow_qty: ln.overflow_qty,
                        counted_total: ln.counted_total, expected_qty: ln.expected_qty, color: ln.color, infinite: ln.infinite, skipped: ln.skipped,
                        moved_out_qty: ln.moved_out_qty, moved_in_qty: ln.moved_in_qty } : row;
        });
        const rows = apply(d.rows, 'home'), over = apply(d.overflow_rows, 'overflow'), extras = apply(d.extra_rows, 'misplaced');
        // lines the screen has no row for yet (a home item with nothing expected, a misplaced piece the laptop could not place)
        for (const ln of lines) {
          const list = ln.role === 'home' ? rows : ln.role === 'overflow' ? over : ln.role === 'misplaced' ? extras : null;
          if (!list || list.some(r => r.wms_id === ln.wms_id)) continue;
          const r = resultById.get(ln.wms_id); const ci = cat?.byId.get(ln.wms_id);
          list.push({ wms_id: ln.wms_id, webshop_id: '', platform: (r?.platform as any) || ci?.platform || 'wgr', article_number: null, picklist_number: null,
                      label: r?.label || ci?.label || String(ln.wms_id), name: null, size: r?.size || ci?.size || null, barcode: null, master_sku: ci?.product || null,
                      home: r?.home || (ci ? sectionDisplay(cat, sectionKey(cat, ci.home)) : null), home_code: r?.home_code || ci?.home || null,
                      role: ln.role as Row['role'], expected_qty: ln.expected_qty, expected_raw: null, live_stock: null, unpicked_qty: 0, in_flight_qty: 0,
                      stock_source: null, infinite: ln.infinite, scanned_saved_qty: ln.scanned_saved_qty, ongoing_qty: ln.ongoing_qty,
                      overflow_qty: ln.overflow_qty, counted_total: ln.counted_total, color: ln.color, fixed: false, line_id: null, resolution: null,
                      write_status: null, skipped: ln.skipped });
        }
        return { ...d, rows, overflow_rows: over, extra_rows: extras };
      });
    }
  }, [cat, tx]);
  const onSessionGone = useCallback((reason: string, unsent: QueuedScan[]) => {
    setSessionId(null);
    const lost = unsent.map(q => { const it = q.local.wms_id != null ? cat?.byId.get(q.local.wms_id) : null; return it ? `${it.label} ${it.size}`.trim() : q.ean; });
    setPopup({ ...POPUP.stopped, sticky: true, title: tx.stopped(reason), big: unsent.length ? tx.notRecorded(unsent.length) : '', small: lost.slice(0, 12).join(' · ') + (lost.length > 12 ? ' …' : '') });
    setFlash(tx.stopped(reason));
    load();
  }, [cat, tx, load]);
  const handlersRef = useRef({ onResults, onSessionGone });
  handlersRef.current = { onResults, onSessionGone };
  const makeQueue = (sid: number) => {
    queueRef.current?.destroy();
    queueRef.current = new ScanQueue(sid, {
      onResults: (r, l, sent) => handlersRef.current.onResults(r, l, sent),
      onSessionGone: (reason, unsent) => handlersRef.current.onSessionGone(reason, unsent),
      onPending: (n, off) => { setPendingN(n); setOffline(off); },
      onError: (msg, dropped) => { setErr(dropped.length ? `${msg} — ${tx.notRecorded(dropped.length)}` : msg); if (dropped.length) load(); },
    });
  };
  // a resumed session (the page was reloaded) gets its queue back, with whatever was still pending
  useEffect(() => { if (sessionId !== null && (!queueRef.current || (queueRef.current as any).sessionId !== sessionId)) makeQueue(sessionId); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (who: string | null) => {
    setBusy(true); setErr('');
    try {
      // a page running an older version reloads first — the count starts on the code the server serves (8.10)
      if (await checkBuild() && reloadFresh()) { setFlash(tx.newVersionReload); return; }
      if (!detailRef.current?.active_session) {
        const g: Gate = await jget(`${INV}/update-gate`).catch(() => ({ on: false, minutes_left: 0, message: null }));
        if (g.on) { setErr(tx.updateInProgress(g.minutes_left)); setBusy(false); return; }
      }
      const d = await jpost(`${INV}/sections/${encodeURIComponent(code)}/start`, who ? { actor: who } : {});
      if (d.detail) setDetail(d.detail);
      setSessionId(d.session_id); setPerson(d.person); setView('count'); setSaved(false); setSummary(null);
      makeQueue(d.session_id);
      if (!cat || (d.catalogue_etag && cat.etag !== d.catalogue_etag)) loadCatalogue(true).catch(() => {});
      if (!d.detail) await load();
      setTimeout(() => scanRef.current?.focus({ preventScroll: true }), 50);
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  const onStart = () => { if (isDevice() && !person) setAsking(true); else start(person); };

  // ── a scan: decided HERE, shown at once, sent in the background ──
  const submitScan = (raw: string) => {
    const ean = raw.trim();
    if (scanRef.current) scanRef.current.value = '';
    typed.current = false;
    if (!ean || !sessionId) return;
    const now = Date.now();
    if (lastScan.current.ean === ean && now - lastScan.current.t < 150) {        // a scanner double-fire; a real second piece is ≥ ~400 ms away
      setFlash(tx.doubleScan); setPopup({ ...POPUP.orange, title: tx.doubleScan, big: '', small: ean }); return;
    }
    lastScan.current = { ean, t: now };
    const d = detailRef.current;
    // a scan continues a paused count (the server does the same)
    if (d?.active_session?.paused_at) setDetail(x => x && x.active_session ? { ...x, active_session: { ...x.active_session, paused_at: null } } : x);
    const dec = decideScan(ean, codes, cat);
    let wms_id: number | null = dec.item?.wms_id ?? null;
    const sectionName = d?.display || code;
    if (dec.status === 'unknown_ean' || dec.status === 'ambiguous') {
      const t = dec.status === 'ambiguous' ? tx.ambiguous : tx.unknown;
      setFlash(t); setPopup({ ...POPUP[dec.status], title: t, big: '', small: ean });
    } else if (dec.status === 'pending' || !dec.item) {
      setFlash(tx.checking); setPopup({ ...POPUP.pending, title: tx.checking, big: '', small: ean });
    } else {
      const it = dec.item;
      const homeDisp = it.home ? sectionDisplay(cat, sectionKey(cat, it.home)) : null;
      let counted = 0, expected = 0, color = 'white', scrollPrefix = 'home';
      // computed NOW from the ref and set as a value — a state updater may run later (review M1), and the popup needs the numbers now
      const prev = detailRef.current;
      if (prev) {
        const bump = (list: Row[], role: Row['role'], expectedDefault: number): [Row[], Row] => {
          const i = list.findIndex(r => r.wms_id === it.wms_id);
          const base: Row = i >= 0 ? list[i] : {
            wms_id: it.wms_id, webshop_id: it.ws, platform: it.platform, article_number: null, picklist_number: null, label: it.label, name: null,
            size: it.size || null, barcode: it.barcode, home: homeDisp, home_code: it.home || null, role, expected_qty: expectedDefault, expected_raw: null,
            live_stock: null, unpicked_qty: 0, in_flight_qty: 0, stock_source: null, infinite: it.nos, scanned_saved_qty: 0, ongoing_qty: 0, overflow_qty: 0, master_sku: it.product || null,
            counted_total: 0, color: 'white', fixed: false, line_id: null, resolution: null, write_status: null, skipped: false };
          const total = base.counted_total + 1;
          const row: Row = { ...base, ongoing_qty: base.ongoing_qty + 1, counted_total: total, skipped: false,
                             color: role === 'overflow' ? 'gray' : colorOf(base.expected_qty, total, role !== 'misplaced', false, base.infinite, false) };
          const out = i >= 0 ? list.map((r, j) => j === i ? row : r) : [...list, row];
          return [out, row];
        };
        let next: Detail;
        if (dec.status === 'home') {
          const [rows, row] = bump(prev.rows, 'home', 0); counted = row.counted_total; expected = row.expected_qty; color = row.color; scrollPrefix = 'home';
          next = { ...prev, rows };
        } else if (dec.status === 'overflow') {
          const [over, row] = bump(prev.overflow_rows, 'overflow', 0); counted = row.counted_total; color = 'gray'; scrollPrefix = 'over';
          next = { ...prev, overflow_rows: over };
        } else {
          const [extras, row] = bump(prev.extra_rows, 'misplaced', 0); counted = row.counted_total; color = 'yellow'; scrollPrefix = 'extra';
          next = { ...prev, extra_rows: extras };
        }
        setDetail(next);
      }
      const st = POPUP[color] || POPUP.white;
      const title = dec.status === 'misplaced' ? tx.wrongPlace : dec.status === 'overflow' ? tx.overflowPopup
        : it.nos ? tx.nosCounted : color === 'green' ? tx.correct : color === 'blue' ? tx.more : color === 'red' ? tx.fewer : '';
      const sizeTxt = it.size ? ` (${it.size})` : '';
      setFlash(dec.status === 'misplaced' ? `⬅ ${it.label}${sizeTxt} ${tx.belongsTo(homeDisp || tx.noHomeShelf)}` : `✓ ${it.label}${sizeTxt}  ${counted}/${it.nos ? '∞' : expected}`);
      setPopup({ ...st, title, big: dec.status === 'misplaced' ? (homeDisp || tx.noHomeShelf) : sectionName,
                 small: `${it.label}${it.size ? ' · ' + it.size : ''}${dec.status === 'home' && !it.nos ? `  ${counted}/${expected}` : ''}` });
      setTimeout(() => document.getElementById(`${scrollPrefix}-${it.wms_id}`)?.scrollIntoView({ block: 'nearest' }), 0);
    }
    const taken = queueRef.current?.push({ cid: newCid(), ean, at: new Date().toISOString(), local: { status: dec.status, wms_id } });
    if (!taken) { setPopup({ ...POPUP.stopped, sticky: true, title: tx.stopped('saved'), big: tx.notRecorded(1), small: ean }); }
    scanRef.current?.focus({ preventScroll: true });
  };
  const onScanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value;
    const now = Date.now();
    if (val.length > 1 && now - lastKey.current > 80) typed.current = true;   // slower than a scanner's burst
    lastKey.current = now;
    if (scanTimer.current) window.clearTimeout(scanTimer.current);
    if (!typed.current && val.trim().length >= 4) scanTimer.current = window.setTimeout(() => submitScan(val), 90);
    if (val.length <= 1) typed.current = false;
  };
  const onScanKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { if (scanTimer.current) window.clearTimeout(scanTimer.current); submitScan(e.currentTarget.value); }
  };

  const applyLines = (lines: LineState[]) => handlersRef.current.onResults([], lines, []);
  const adjust = async (r: Row, delta: number) => {
    if (adjusting.current) return;               // one at a time: the server applies the delta, nothing is lost
    adjusting.current = true;
    try {
      const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/adjust`, { wms_id: r.wms_id, delta, actor: person || undefined });
      setDetail(d => d ? { ...d, rows: d.rows.map(row => row.wms_id === r.wms_id && row.role === r.role ? { ...row, counted_total: x.scanned_total + row.overflow_qty, ongoing_qty: Math.max(0, row.ongoing_qty + delta), color: x.color } : row),
                            overflow_rows: d.overflow_rows.map(row => row.wms_id === r.wms_id && r.role === 'overflow' ? { ...row, counted_total: x.scanned_total, color: x.color } : row),
                            extra_rows: d.extra_rows.map(row => row.wms_id === r.wms_id && r.role === 'misplaced' ? { ...row, counted_total: x.scanned_total, color: x.color } : row) } : d);
    } catch (e: any) { if (!/No active session/i.test(e.message)) setErr(e.message); else { setSessionId(null); load(); } }
    adjusting.current = false;
    scanRef.current?.focus({ preventScroll: true });
  };
  const siblingsOf = (r: Row) => (detailRef.current?.rows || []).filter(x => x.role === 'home' && productKey(x) === productKey(r)).map(x => x.wms_id);
  const toggleMove = (id: number) => setMoveSel(m => { const n = new Set(m); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doMove = async (target: string, ids: number[], mt: MoveTarget) => {
    const d = detailRef.current; if (!d || !ids.length) return;
    const all = [...d.rows, ...d.extra_rows];
    // not every size of a product moving? ask (Fernando, 8.10: jeans sizes split across sections on purpose)
    const groups = new Map<string, { label: string; moving: string[]; staying: string[] }>();
    for (const x of d.rows.filter(x => x.role === 'home' && (x.counted_total > 0 || x.expected_qty > 0))) {
      const k = productKey(x); const g = groups.get(k) || { label: x.name || x.master_sku || x.label, moving: [], staying: [] };
      (ids.includes(x.wms_id) ? g.moving : g.staying).push(x.size || '—'); groups.set(k, g);
    }
    const partial = Array.from(groups.entries()).filter(([, g]) => g.moving.length && g.staying.length);
    try {
      if (partial.length && !window.confirm(partial.map(([, g]) => tx.movePartial(g.label, g.moving.join(', '), g.staying.join(', '))).join('\n'))) return;
      setBusy(true); setErr('');
      const left = queueRef.current ? await queueRef.current.drain(5000) : 0;
      if (left > 0) { setErr(tx.pendingScans(left)); return; }
      if (mt.state === 'not_counted') {
        // move before scanning (9.10): the target counts the pieces; anything scanned of them here is cleared first
        const scanned = all.filter(r => ids.includes(r.wms_id) && r.counted_total > 0);
        const n = scanned.reduce((k, r) => k + r.counted_total, 0);
        if (n > 0 && !window.confirm(tx.clearScannedConfirm(n, mt.display))) return;
        const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/moves`, { to: target, wms_ids: ids, unscanned: true, clear_scanned: n > 0, actor: person || undefined });
        setMoveSel(new Set()); setFlash(tx.movedUnscanned(x.to_display)); setNotice(tx.movedUnscanned(x.to_display));
        await load();
        return;
      }
      const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/moves`, { to: target, wms_ids: ids, actor: person || undefined });
      setDetail(dd => dd ? { ...dd, moves: x.pending } : dd);
      setFlash(tx.movePending(x.to_display)); setMoveSel(new Set());
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); scanRef.current?.focus({ preventScroll: true }); }
    void all;
  };
  const cancelMove = async (id: number) => {
    try {
      const r = await authFetch(`${INV}/sections/${encodeURIComponent(code)}/moves/${id}`, { method: 'DELETE' });
      const x = await r.json().catch(() => ({}));
      if (!r.ok) throw apiError(r.status, x);
      setDetail(dd => dd ? { ...dd, moves: x.pending } : dd);
      if (x.status === 'reverted') { setNotice(''); await load(); }          // an unscanned move undone: its rows are back
    } catch (e: any) { setErr(e.message); }
    scanRef.current?.focus({ preventScroll: true });
  };
  const onRowAction = async (a: RowAction, r: Row) => {
    setMenuId(null);
    try {
      if (a === 'move') { toggleMove(r.wms_id); return; }
      if (a === 'here' || a === 'home') {
        // a wrong-place row: Move here = the item lives on this section from Save count; Move home = carried home
        setBusy(true); setErr('');
        const left = queueRef.current ? await queueRef.current.drain(5000) : 0;
        if (left > 0) { setErr(tx.pendingScans(left)); return; }
        const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/moves`,
                              a === 'here' ? { here: true, wms_ids: [r.wms_id], actor: person || undefined }
                                           : { to: r.home_code, wms_ids: [r.wms_id], actor: person || undefined });
        setDetail(dd => dd ? { ...dd, moves: x.pending } : dd);
        setMoveSel(m => { const n = new Set(m); n.delete(r.wms_id); return n; });
        setFlash(a === 'here' ? tx.herePending(x.to_display) : tx.movePending(x.to_display));
        return;
      }
      const ids = a === 'skip_size' || a === 'unskip' || r.role === 'misplaced' ? [r.wms_id] : siblingsOf(r);
      if (a === 'clear_all') {
        if (!window.confirm(tx.clearProductConfirm(r.label, ids.length))) return;
        const left = queueRef.current ? await queueRef.current.drain(5000) : 0;
        if (left > 0) { setErr(tx.pendingScans(left)); return; }           // scans still travelling would land on the cleared lines (review L11)
        const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/clear`, { scope: 'product', wms_ids: ids, actor: person || undefined });
        applyLines(x.lines || []);
      } else {
        const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/skip`, { wms_ids: ids, skip: a !== 'unskip', actor: person || undefined });
        applyLines(x.lines || []);
      }
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); scanRef.current?.focus({ preventScroll: true }); }                   // also after Cancel on the confirm (review M7)
  };
  const clearSection = async () => {
    try {
      if (!window.confirm(tx.clearSectionConfirm(detail?.display || code))) return;
      setBusy(true);
      const left = queueRef.current ? await queueRef.current.drain(5000) : 0;
      if (left > 0) { setErr(tx.pendingScans(left)); return; }
      await jpost(`${INV}/sections/${encodeURIComponent(code)}/clear`, { scope: 'section', actor: person || undefined }); await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); scanRef.current?.focus({ preventScroll: true }); }
  };
  const readyToFinish = async () => {
    setBusy(true); setErr('');
    try {
      const left = queueRef.current ? await queueRef.current.drain(10000) : 0;
      if (left > 0) { setErr(tx.pendingScans(left)); setBusy(false); return; }
      const sm: Summary = await jpost(`${INV}/sections/${encodeURIComponent(code)}/preview`, { actor: person || undefined });
      setSummary(sm); setSaved(false); setView('conclusion');
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  const saveCount = async () => {
    setBusy(true); setErr('');
    try {
      const left = queueRef.current ? await queueRef.current.drain(10000) : 0;
      const sm = await jpost(`${INV}/sections/${encodeURIComponent(code)}/save`, { done: true, pending: left, plan_token: summary?.shop?.plan_token });
      queueRef.current?.destroy(); queueRef.current = null;
      setSummary(sm); setSaved(true); setSessionId(null);
      load();
    } catch (e: any) {
      if (e.status === 409 && e.reason === 'plan_changed') {
        // what would be written changed since the check: nothing was saved — show the new numbers
        setBusy(false); await readyToFinish(); setErr(tx.shop.changed); return;
      }
      setErr(e.message);
    }
    setBusy(false);
  };
  // after Save the writes are sent in the background: the Shop block follows them until nothing is pending
  const pendingShop = saved ? (summary?.shop?.pending?.length || 0) : 0;
  useEffect(() => {
    if (!pendingShop) return;
    let n = 0;
    const t = window.setInterval(async () => {
      n += 1;
      try {
        const shop: Shop = await jget(`${INV}/sections/${encodeURIComponent(code)}/shop`);
        setSummary(sm => sm ? { ...sm, shop } : sm);
        if (!shop.pending.length) window.clearInterval(t);
      } catch { /* the next tick tries again */ }
      if (n >= 40) window.clearInterval(t);
    }, 3000);
    return () => window.clearInterval(t);
  }, [pendingShop, code]);
  const markDone = async (done: boolean) => { setBusy(true); try { await jpost(`${INV}/sections/${encodeURIComponent(code)}/done`, { done }); setView('count'); await load(); } catch (e: any) { setErr(e.message); } setBusy(false); };
  // Pause / Continue (9.10): the counting time stops; the count stays open (the picker hold and the update gate wait)
  const togglePause = async () => {
    setBusy(true); setErr('');
    try {
      if (paused) {
        await jpost(`${INV}/sections/${encodeURIComponent(code)}/continue`, { actor: person || undefined });
        setDetail(d => d && d.active_session ? { ...d, active_session: { ...d.active_session, paused_at: null } } : d);
      } else {
        const left = queueRef.current ? await queueRef.current.drain(5000) : 0;
        if (left > 0) { setErr(tx.pendingScans(left)); return; }
        const x = await jpost(`${INV}/sections/${encodeURIComponent(code)}/pause`, { actor: person || undefined });
        setDetail(d => d && d.active_session ? { ...d, active_session: { ...d.active_session, paused_at: x.paused_at } } : d);
      }
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); scanRef.current?.focus({ preventScroll: true }); }
  };
  // Cancel count (9.10): everything this count changed is undone; the section is as it was before Start
  const cancelCount = async () => {
    if (!window.confirm(tx.cancelCountConfirm(detail?.display || code))) return;
    setBusy(true); setErr('');
    try {
      await queueRef.current?.drain(5000);
      await jpost(`${INV}/sections/${encodeURIComponent(code)}/cancel`, { actor: person || undefined });
      queueRef.current?.destroy(); queueRef.current = null; setPendingN(0);
      setSessionId(null); setMoveSel(new Set()); setView('count'); setSummary(null); setPopup(null);
      setNotice(tx.cancelled(detail?.display || code));
      await load();
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  };
  const resetCount = async () => {
    if (!window.confirm(`Reset the count of ${detail?.display || code}?\n\nEvery counted quantity of this section goes back to 0 (the scan history stays). For a recount.`)) return;
    setBusy(true); try { await jpost(`${INV}/sections/${encodeURIComponent(code)}/reset`, {}); await load(); } catch (e: any) { setErr(e.message); } setBusy(false);
  };

  if (asking) return <WhoIsWorking title={tx.whoCounts} onPick={p => { setPerson(p.username); setAsking(false); start(p.username); }} onCancel={() => setAsking(false)} />;

  const rows = detail?.rows ?? [], over = detail?.overflow_rows ?? [], extras = detail?.extra_rows ?? [], found = detail?.found_elsewhere ?? [];
  const othersSession = !!detail?.active_session && !scanning;      // someone else is counting here
  // ± while counting; after a save, as long as nobody else counts and the section is not done
  const canEdit = scanning || (!!detail && !detail.done && !othersSession && !isDevice());
  const leaveLocked = scanning;                                       // Prev / Next / back: finish first
  const pendingMoves = (detail?.moves || []).filter(m => (m.status === 'pending' && (m.direction === 'out' || m.direction === 'here'))
                                                       || (m.kind === 'unscanned' && m.status === 'applied' && m.direction === 'out' && m.undoable));
  const menu: RowMenu = { siblingsOf: (r: Row) => siblingsOf(r).length, openId: menuId, onToggle: (id: number) => setMenuId(m => m === id ? null : id), onAction: onRowAction,
                          moving: new Map(pendingMoves.map(m => [m.wms_id, m] as [number, Move])), selected: moveSel, sectionName: detail?.display || code,
                          onCancelMove: (id: number) => { cancelMove(id); } };
  const moveRows = [...rows, ...extras].filter(r => moveSel.has(r.wms_id));

  const actions = (
    <div className={`flex flex-wrap items-center gap-2 ${phone ? 'fixed bottom-0 left-0 right-0 z-40 bg-[#0f1117]/95 border-t border-gray-800 p-3' : 'mb-4'}`}>
      {!scanning
        ? <button onClick={onStart} disabled={busy || othersSession} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold rounded-lg">{tx.start}</button>
        : <button onClick={readyToFinish} disabled={busy} className="px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">{busy ? '…' : tx.readyToFinish}</button>}
      {scanning && <button onClick={togglePause} disabled={busy} className={`px-3 py-2.5 text-sm rounded-lg ${paused ? 'bg-amber-600 hover:bg-amber-500 text-black font-semibold' : 'bg-gray-800 hover:bg-gray-700 text-gray-200'}`}>{paused ? `▶ ${tx.resume}` : `⏸ ${tx.pause}`}</button>}
      {scanning && <button onClick={clearSection} disabled={busy} className="px-3 py-2.5 bg-gray-800 hover:bg-red-900 text-gray-400 hover:text-white text-sm rounded-lg">{tx.clearSection}</button>}
      {scanning && <button onClick={cancelCount} disabled={busy} className="px-3 py-2.5 bg-gray-800 hover:bg-red-900 text-red-300 hover:text-white text-sm rounded-lg">{tx.cancelCount}</button>}
      {detail?.done && !scanning && <button onClick={() => markDone(false)} disabled={busy} className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg">{tx.reopen}</button>}
      {elevated() && !scanning && !othersSession && (rows.some(r => r.counted_total > 0)) && <button onClick={resetCount} disabled={busy} className="px-3 py-2.5 bg-gray-800 hover:bg-red-900 text-gray-400 hover:text-white text-sm rounded-lg">Reset count</button>}
      <div className="flex-1" />
      <button onClick={() => prevSec && goTo(prevSec.code)} disabled={!prevSec || leaveLocked} title={leaveLocked ? 'Finish the count first' : tx.prev} className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 text-gray-300 text-sm rounded-lg">‹ {prevSec ? prevSec.display : tx.prev}</button>
      <button onClick={() => nextSec && goTo(nextSec.code)} disabled={!nextSec || leaveLocked} title={leaveLocked ? 'Finish the count first' : tx.next} className="px-3 py-2.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 text-gray-300 text-sm rounded-lg">{nextSec ? nextSec.display : tx.next} ›</button>
    </div>
  );

  const header = (
    <>
      <button onClick={() => { if (!leaveLocked || window.confirm('Leave without finishing? The scans of this session stay on the server until Save count.')) navigate('/tools/wgr-inventory/work-area'); }}
        className="text-gray-500 hover:text-white text-sm mb-3">← {tx.workArea}</button>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-2xl font-bold text-white">{detail?.display || stockplace}
          {detail?.alias && detail.alias !== detail.display && <span className="text-gray-500 text-lg font-normal"> / {detail.alias}</span>}
          {detail?.done && <span className="ml-3 text-sm text-green-400 font-normal">✓ done</span>}
        </h1>
        <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
          {detail?.aisle && <span>aisle {detail.aisle} · </span>}{detail?.unit_id && <span>{detail.unit_id} · </span>}
          {detail && !detail.in_plan && <span className="text-amber-400">not in the floor plan · </span>}
          {detail?.reserved && <span className="text-red-300">reserved hole — should be empty · </span>}
          {person && <span>{tx.whoCounts.replace('?', '')}: <b className="text-gray-300">{person}</b> {isDevice() && !scanning && <button onClick={() => setAsking(true)} className="underline">{tx.change}</button>}</span>}
          <CatalogueChip tx={tx} />
        </div>
      </div>
      {err && <div className="mb-3 px-3 py-2 rounded-lg bg-red-950/50 border border-red-800 text-red-300 text-sm">{err}</div>}
      {notice && <div className="mb-3 px-3 py-2 rounded-lg bg-sky-950/40 border border-sky-800 text-sky-200 text-sm flex justify-between gap-3"><span>{notice}</span><button onClick={() => setNotice('')} className="text-sky-400 hover:text-white">✕</button></div>}
    </>
  );

  if (view === 'conclusion' && summary) {
    return (
      <div className="p-3 md:p-6">
        {header}
        <Conclusion sum={summary} saved={saved} busy={busy} tx={tx} onBack={() => { setView('count'); setTimeout(() => scanRef.current?.focus({ preventScroll: true }), 50); }}
                    onSave={saveCount} onNext={nextSec ? () => goTo(nextSec.code) : null} nextLabel={nextSec ? `${nextSec.display} ›` : tx.nextSection}
                    onPrev={prevSec ? () => goTo(prevSec.code) : null} prevLabel={prevSec ? `‹ ${prevSec.display}` : tx.prevSection}
                    onWorkArea={() => navigate('/tools/wgr-inventory/work-area')} />
      </div>
    );
  }

  return (
    <div className={`p-3 md:p-6 ${phone ? 'pb-28' : ''}`} onClick={() => { if (menuId !== null) setMenuId(null); }}>
      {header}
      {detail?.stock_source === 'sync' && <div className="mb-3 px-3 py-2 rounded-lg bg-amber-950/40 border border-amber-800 text-amber-200 text-sm">{tx.syncHint}</div>}
      {othersSession && detail?.active_session && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-blue-950/40 border border-blue-800 text-blue-200 text-sm">Being counted by {detail.active_session.person}{detail.active_session.device ? ` on ${detail.active_session.device}` : ''} — wait for their Save.</div>
      )}
      {!phone && actions}
      {paused && <div className="mb-3 px-3 py-2 rounded-lg bg-amber-950/50 border border-amber-700 text-amber-200 text-sm font-medium">⏸ {tx.pausedBanner}</div>}
      {scanning && (
        <div className="mb-4 flex items-center gap-3 flex-wrap sticky top-0 z-20 bg-[#0f1117] py-2">
          <input ref={scanRef} onChange={onScanChange} onKeyDown={onScanKey} autoFocus placeholder={tx.scanPlaceholder} autoComplete="off"
            inputMode={phone ? 'text' : 'none'}
            className="w-full sm:w-72 bg-gray-900 border-2 border-blue-600 text-white rounded-xl px-4 py-2.5 focus:outline-none font-mono" />
          {flash && <span className="text-sm text-gray-300">{flash}</span>}
          {pendingN > 0 && <span className={`text-xs px-2 py-1 rounded-full ${offline ? 'bg-yellow-900/60 text-yellow-200' : 'bg-gray-800 text-gray-400'}`}>{offline ? tx.offlineWaiting(pendingN) : tx.pendingScans(pendingN)}</span>}
        </div>
      )}
      {scanning && moveRows.length > 0 && (
        <MoveBar rows={moveRows} nb={nb} code={code} sections={sections} tx={tx} busy={busy} onMove={doMove} onCancel={() => { setMoveSel(new Set()); setTimeout(() => scanRef.current?.focus({ preventScroll: true }), 0); }} onToggleRow={toggleMove} />
      )}
      {scanning && pendingMoves.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 items-center text-xs">
          <span className="text-sky-300">{tx.summary.moves}:</span>
          {pendingMoves.map(m => <span key={m.id} className="px-2 py-1 rounded-full bg-sky-900/60 text-sky-100">{m.label}{m.size ? ` ${m.size}` : ''} × {m.qty} → {m.to}{m.kind === 'unscanned' ? ` (${tx.notScanned})` : ''}
            <button {...noFocus} onClick={() => cancelMove(m.id)} className="ml-1.5 text-sky-300 hover:text-white" title={tx.cancelMove}>✕</button></span>)}
          <span className="text-gray-500">· {tx.summary.movesPending}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-3">
        {Object.entries(tx.legend).map(([c, label]) => <span key={c} className="flex items-center gap-1.5 text-xs text-gray-400"><span className={`w-3 h-3 rounded-sm ${DOT[c]}`} />{label}</span>)}
        <span className="text-xs text-gray-600">{detail?.article_count ?? 0} items · {tx.expected.toLowerCase()} {detail?.expected_total ?? 0}</span>
      </div>
      <RowTable list={rows} tx={tx} canEdit={canEdit} onAdjust={adjust} idPrefix="home" menu={scanning ? menu : undefined} />
      {over.length > 0 && <><h2 className="text-sm font-semibold text-gray-300 mt-6 mb-2">{tx.mayBeHere} ({over.length})</h2>
        <RowTable list={over} tx={tx} canEdit={canEdit && scanning} onAdjust={adjust} homeCol="home" idPrefix="over" /></>}
      {extras.length > 0 && <><h2 className="text-sm font-semibold text-yellow-400 mt-6 mb-2">{tx.shouldntBeHere} ({extras.length}) — {tx.setAside.toLowerCase()}</h2>
        <RowTable list={extras} tx={tx} canEdit={canEdit && scanning} onAdjust={adjust} homeCol="home" idPrefix="extra" menu={scanning ? menu : undefined} /><p className="text-xs text-gray-500 mt-1">{tx.fixedByManager}</p></>}
      {found.length > 0 && <><h2 className="text-sm font-semibold text-blue-300 mt-6 mb-2">{tx.foundElsewhere} ({found.length})</h2>
        <RowTable list={found} tx={tx} canEdit={false} onAdjust={adjust} homeCol="found" idPrefix="found" /></>}
      {phone && actions}
      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => { setPopup(null); scanRef.current?.focus({ preventScroll: true }); }}>
          <div className={`${popup.bg} ${popup.fg} rounded-2xl shadow-2xl px-12 py-8 text-center max-w-md`} onClick={e => e.stopPropagation()}>
            {popup.title && <div className="text-3xl font-extrabold mb-3">{popup.title}</div>}
            {popup.big && <div className="text-5xl font-black tracking-wide">{popup.big}</div>}
            <div className="text-lg font-mono mt-3 opacity-90">{popup.small}</div>
            {popup.sticky && <button onClick={() => { setPopup(null); }} className="mt-5 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm">OK</button>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Statistics (manager / admin) ──────────────────────────────────────────────
function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1.5">{label}</div>
      <div className={`text-2xl font-bold ${tone || 'text-white'}`}>{value}</div>
    </div>
  );
}

export function WgrStatistics() {
  const [s, setS] = useState<any | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { jget(`${INV}/statistics`).then(setS).catch(e => setErr(e.message)); }, []);
  const v = s?.volume, sp = s?.speed, i = s?.issues;
  const n = (x: any) => (x === null || x === undefined ? '…' : Number(x).toLocaleString());
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-1">Inventory</h1>
      <p className="text-gray-500 text-sm mb-5">Statistics — round {s?.round_id ?? '…'}</p>
      <Nav active="statistics" />
      {err && <div className="text-red-400 text-sm mb-3">{err}</div>}
      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Volume</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 max-w-4xl">
        <Stat label="Items counted" value={n(v?.items_counted)} /><Stat label="Items to count" value={n(v?.items_to_count)} />
        <Stat label="Items left" value={n(v?.items_left)} /><Stat label="Sessions" value={n(v?.sessions)} />
        <Stat label="Sections counted" value={n(v?.sections_counted)} /><Stat label="Sections to count" value={n(v?.sections_to_count)} />
        <Stat label="Sections left" value={n(v?.sections_left)} />
      </div>
      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Speed</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 max-w-4xl">
        <Stat label="Scans / hour" value={n(sp?.items_per_hour)} /><Stat label="Sections / hour" value={n(sp?.sections_per_hour)} />
        <Stat label="Hours worked" value={n(sp?.hours_worked)} /><Stat label="Est. time left" value={sp?.time_left_hours != null ? `${sp.time_left_hours} h` : '—'} tone="text-blue-300" />
      </div>
      <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Issues</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mb-6">
        <Stat label="Items missing" value={n(i?.items_missing)} tone="text-red-300" /><Stat label="Found (surplus)" value={n(i?.items_surplus)} tone="text-blue-300" />
        <Stat label="Wrong section" value={n(i?.items_wrong_section)} tone="text-yellow-300" /><Stat label="Problem sections" value={n(i?.problem_sections)} tone="text-orange-300" />
        <Stat label="Resolved pieces" value={n(i?.fixed_items)} tone="text-green-300" /><Stat label="Would write (ready)" value={n(i?.would_write)} tone="text-amber-300" />
        <Stat label="Waiting (held)" value={n(i?.held)} tone="text-amber-300" /><Stat label="Writes to the shops" value={i ? `${n(i.written)} (+${n(i.pieces_written_plus)} / ${n(i.pieces_written_minus)})` : '…'} tone="text-green-300" />
        <Stat label="In Review" value={n(i?.in_review)} tone="text-red-300" /><Stat label="Cancelled counts" value={n(i?.cancelled_counts)} tone="text-gray-300" />
      </div>
      {s?.per_shop?.length > 0 && <div className="text-sm text-gray-400">{s.per_shop.map((x: any) => `${SHOP[x.platform] || x.platform}: ${x.counted} counted of ${x.expected} expected on ${x.lines} lines`).join(' · ')}</div>}
      <p className="text-gray-600 text-xs mt-4 max-w-3xl">“Items to count” = the synced pieces of Motley and Nora on the floor plan's sections; a section's expected quantity at Start is the shop's live stock + the orders not yet picked.</p>
    </div>
  );
}

// ── Manager (manager / admin) ─────────────────────────────────────────────────
interface ItemRow extends ShopRow { webshop_id: string | null; home: string | null;
  writes: { id: number; delta: number; status: string; reason: string | null; trigger: string | null; by: string | null; at: string | null }[] }
interface WritesCfg { mode: 'on' | 'off'; first_write: { wms_id: number; write_id: number; delta: number; by: string; at: string } | null; set_by: string | null; set_at: string | null }
const ITEM_STATUS: Record<string, [string, string]> = {
  would_write: ['ready to write', 'bg-amber-900/60 text-amber-200'], review: ['failed — Review', 'bg-red-900/60 text-red-200'],
  pending: ['sending', 'bg-gray-700 text-gray-200'], held: ['waiting', 'bg-gray-800 text-gray-300'], written: ['written', 'bg-green-900/60 text-green-200'],
  nothing: ['matches', 'bg-gray-800 text-gray-500'], infinite: ['infinite (WGR)', 'bg-gray-800 text-gray-500'],
  open: ['in progress', 'bg-gray-800 text-gray-400'], skipped: ['skipped — stock trusted', 'bg-teal-900/60 text-teal-200'],
};
interface MisRow { line_id: number; found_at: string; belongs_to: string | null; label: string; size: string | null; platform: string; qty: number;
  fixed: boolean; resolution: string | null; resolved_by: string | null; found_by: string | null; created_at: string | null; home_done: boolean; hint: string }

const RESOLVE_TEXT: Record<string, (m: MisRow) => string> = {
  move_home: m => `${m.label}: carried to ${m.belongs_to} — credit its count there?`,
  found_here: m => `${m.label} LIVES at ${m.found_at} now?\n\nThe shelf is written in the WMS${m.platform === 'wgr' ? ' and pushed to WGR as its stockPlace' : ' (Nora: WMS only)'}.`,
  overflow: m => `${m.found_at} becomes a restock shelf for ${m.label} (its home stays ${m.belongs_to})?`,
};

export function WgrManager() {
  const [tab, setTab] = useState<'items' | 'misplaced' | 'unlocated' | 'sessions' | 'rounds' | 'settings'>('items');
  const [items, setItems] = useState<ItemRow[]>([]);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [itemFilter, setItemFilter] = useState<string>('would_write');
  const [writes, setWrites] = useState<WritesCfg | null>(null);
  const [catchUp, setCatchUp] = useState<any | null>(null);
  const [mis, setMis] = useState<MisRow[]>([]);
  const [misFixed, setMisFixed] = useState('false');
  const [unl, setUnl] = useState<any | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [rounds, setRounds] = useState<any[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const loadItems = useCallback((refresh = false) => jget(`${INV}/items?status=${itemFilter}${refresh ? '&refresh=true' : ''}`)
    .then(d => { setItems(d.rows || []); setItemCounts(d.counts || {}); setWrites(d.writes || null); }).catch(e => setMsg(e.message)), [itemFilter]);
  const loadDiffs = loadItems;
  const loadMis = useCallback(() => jget(`${INV}/misplaced?fixed=${misFixed}`).then(d => setMis(d.rows || [])).catch(e => setMsg(e.message)), [misFixed]);
  const loadSessions = useCallback(() => jget(`${INV}/sessions`).then(d => setSessions(d.rows || [])).catch(e => setMsg(e.message)), []);
  const loadRounds = useCallback(() => jget(`${INV}/rounds`).then(d => setRounds(d.rows || [])).catch(e => setMsg(e.message)), []);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadMis(); }, [loadMis]);
  useEffect(() => {
    if (tab === 'unlocated' && !unl) jget(`${INV}/unlocated`).then(setUnl).catch(e => setMsg(e.message));
    if (tab === 'sessions') loadSessions();
    if (tab === 'rounds') loadRounds();
  }, [tab, unl, loadSessions, loadRounds]);

  // one item now — the FIRST write is made this way, watched in the shop's stock log (then the switch may go ON)
  const writeNow = async (r: ItemRow) => {
    if (!window.confirm(`Write ${sign(r.delta)} for ${r.label}${r.size ? ' ' + r.size : ''} to ${SHOP[r.platform || ''] || r.platform} now?\n\n`
      + `counted ${r.counted} · expected ${r.expected} · already written ${sign(r.written)}\n\nOne relay write, logged; watch the shop's stock log.`)) return;
    setBusy(true); setMsg('');
    try {
      const d = await jpost(`${INV}/items/${r.wms_id}/write`, { expect_delta: r.delta });
      setMsg(d.result ? `${r.label} ${r.size || ''}: ${sign(d.result.delta)} → ${d.result.status}${d.result.reason ? ` (${d.result.reason})` : ''}${d.result.route ? ` via ${d.result.route}` : ''}` : 'Nothing sent.');
      await loadItems();
    } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  // the ready items in one relay batch (at most 200) — only after the first write by hand
  const writeReady = async () => {
    setBusy(true); setMsg('');
    try {
      const d = await jget(`${INV}/items?status=would_write`);
      const rows: ItemRow[] = (d.rows || []).slice(0, 200);
      if (!rows.length) { setMsg('Nothing ready to write.'); return; }
      const plus = rows.reduce((n, r) => n + Math.max(0, r.delta), 0), minus = rows.reduce((n, r) => n + Math.min(0, r.delta), 0);
      if (!window.confirm(`Write ${rows.length} item${rows.length === 1 ? '' : 's'} (+${plus} / ${minus} pcs) to the shops in one relay batch?`)) return;
      const x = await jpost(`${INV}/catch-up`, { expect: Object.fromEntries(rows.map(r => [r.wms_id, r.delta])) });
      setMsg(`${x.queued} write${x.queued === 1 ? '' : 's'} sent — the list refreshes in a few seconds.`);
      window.setTimeout(() => { loadItems(); }, 6000);
    } catch (e: any) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const loadCatchUp = async () => {
    setBusy(true); setMsg('');
    try { setCatchUp(await jget(`${INV}/catch-up`)); await loadItems(); } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  const recheck = async (codes: string[]) => {
    if (!window.confirm(`Re-read ${codes.length} section${codes.length === 1 ? '' : 's'} saved before the per-item check?\n\nOnly their unchecked lines: the shops are read now and the movement since each line's count is explained by the events, like a Save.`)) return;
    setBusy(true); setMsg('');
    try { const d = await jpost(`${INV}/catch-up/repairs`, { recheck: codes }); setMsg(`Re-read: ${d.rechecked.map((x: any) => `${x.display || x.code} ${x.lines_checked ?? x.skipped}`).join(' · ')}`); setCatchUp(await jget(`${INV}/catch-up`)); await loadItems(); }
    catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  const setMode = async (mode: 'on' | 'off') => {
    if (!window.confirm(mode === 'on' ? 'Switch shop writes ON?\n\nFrom now on every Save count writes its ready items to the shops.' : 'Switch shop writes OFF?\n\nSave count only records what would be written; writes already queued from a Save are not sent.')) return;
    setBusy(true); setMsg('');
    try { setWrites(await jpost(`${INV}/settings/writes`, { mode })); } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  const resolve = async (m: MisRow, action: 'move_home' | 'found_here' | 'overflow') => {
    if (!window.confirm(RESOLVE_TEXT[action](m))) return;
    setBusy(true); setMsg('');
    try {
      const d = await jpost(`${INV}/misplaced/resolve`, { line_id: m.line_id, action });
      setMsg(action === 'move_home' ? `${m.label}: ${d.credited} credited at ${d.home}.`
        : action === 'found_here' ? `${m.label}: shelf is now ${d.shelf}${d.was ? ` (was ${d.was})` : ''}${d.pushed_to_shop ? ', pushed to the shop' : d.note ? ` — ${d.note}` : ''}.`
        : `${m.label}: restock shelves now ${d.overflow}.`);
      await loadMis(); await loadItems();
    } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };
  const closeRound = async () => {
    const label = window.prompt('Close this round and open the next?\n\nNothing is deleted: its counts, sessions and misplaced pieces stay under the old round. Give the closed round a label (optional):');
    if (label === null) return;
    setBusy(true);
    try {
      const d = await jpost(`${INV}/new-inventory`, { label });
      setMsg(`Round ${d.closed_round} closed, round ${d.new_round} open.`);
      await Promise.all([loadItems(), loadMis(), loadSessions(), loadRounds()]); setUnl(null); setTab('rounds');
    } catch (e: any) { setMsg(e.message); }
    setBusy(false);
  };

  const Tabs = (['items', 'misplaced', 'unlocated', 'sessions', 'rounds', 'settings'] as const);
  void loadDiffs;
  const placesTxt = (r: ItemRow) => r.places.map(pl => `${pl.display} ${pl.counted}${pl.done ? '' : pl.being_counted_by ? ' ●' : ' ○'}`).join(' · ');
  const holdsTxt = (r: ItemRow) => r.holds.map(h => h.text).join('; ');
  return (
    <div className="p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-2xl font-bold text-white">Inventory</h1>
        {role() === 'admin' && <button onClick={closeRound} disabled={busy} className="px-4 py-2 bg-red-800 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">Close round · start the next</button>}
      </div>
      <p className="text-gray-500 text-sm mb-5">Manager</p>
      <Nav active="manager" />
      <div className="flex gap-1 mb-4 border-b border-gray-800 pb-3">
        {Tabs.map(t => <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full text-sm ${tab === t ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white'}`}>
          {{ items: 'Items', misplaced: 'Misplaced', unlocated: 'Not countable', sessions: 'Sessions', rounds: 'Rounds', settings: 'Settings' }[t]}</button>)}
      </div>
      {msg && <div className="mb-3 text-sm text-gray-300 break-words">{msg}</div>}

      {tab === 'items' && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={itemFilter} onChange={e => setItemFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5">
              {['would_write', 'review', 'pending', 'held', 'open', 'written', 'skipped', 'nothing', ''].map(k => <option key={k} value={k}>{k ? `${ITEM_STATUS[k]?.[0] || k} (${itemCounts[k] || 0})` : 'All'}</option>)}
            </select>
            <button onClick={() => loadItems(true)} disabled={busy} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg">Decide all again</button>
            {role() === 'admin' && <button onClick={writeReady} disabled={busy || !writes?.first_write || !(itemCounts.would_write > 0)}
              title={writes?.first_write ? '' : 'Make the first write by hand first (Write now on one item)'}
              className="px-3 py-1.5 bg-green-800 hover:bg-green-700 disabled:opacity-40 text-white text-sm rounded-lg">Write ready ({Math.min(200, itemCounts.would_write || 0)})</button>}
            {role() === 'admin' && <button onClick={loadCatchUp} disabled={busy} className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm rounded-lg">Round check (catch-up)</button>}
            <span className={`text-xs px-2 py-1 rounded-full ${writes?.mode === 'on' ? 'bg-green-900/60 text-green-200' : 'bg-gray-800 text-gray-400'}`}>writes {writes?.mode === 'on' ? 'ON' : 'OFF'}</span>
          </div>
          <p className="text-xs text-gray-500 mb-3 max-w-4xl">Each item is decided across the whole round — every place it may be (home, restock shelves, wherever it was found) must be counted. The difference (counted − expected − already written) is written to its shop at Save count while writes are ON; ○ = place not counted yet, ● = being counted.</p>
          {catchUp && (
            <div className="mb-4 bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3 text-sm text-gray-300 space-y-2">
              <div><b className="text-white">Round {catchUp.round_id}:</b> {catchUp.items} items with a count · {Object.entries(catchUp.counts).map(([k, v]) => `${ITEM_STATUS[k]?.[0] || k} ${v}`).join(' · ')}
                · ready to write {catchUp.would_write.items} (+{catchUp.would_write.plus} / {catchUp.would_write.minus} pcs)</div>
              {Object.keys(catchUp.held_by || {}).length > 0 && <div className="text-xs text-amber-300">waiting because: {Object.entries(catchUp.held_by).map(([k, v]) => `${k.replace('_', ' ')} ${v}`).join(' · ')}</div>}
              {catchUp.recheck.length > 0 && <div className="text-xs">Saved before the per-item check (need the Save re-read): {catchUp.recheck.map((x: any) => `${x.display} (${x.lines})`).join(', ')}
                {' '}<button onClick={() => recheck(catchUp.recheck.filter((x: any) => !x.active).map((x: any) => x.code))} disabled={busy} className="ml-2 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded-md">Re-read {catchUp.recheck.filter((x: any) => !x.active).length}</button></div>}
              {catchUp.dropped_credits.length > 0 && <div className="text-xs text-gray-500">Moves into sections not counted yet — the credit was taken back (085), the target counts the pieces: {catchUp.dropped_credits.map((m: any) => `${m.from} → ${m.to} ×${m.qty}`).join(', ')}</div>}
            </div>
          )}
          <PhoneCards rows={items} keyOf={r => r.wms_id} card={r => (
            <div>
              <div className="flex justify-between gap-2"><span className="text-white font-semibold">{r.label}{r.size ? ` · ${r.size}` : ''}</span>
                <span className={`font-bold ${r.delta > 0 ? 'text-blue-300' : r.delta < 0 ? 'text-red-300' : 'text-gray-400'}`}>{r.delta ? sign(r.delta) : ''}</span></div>
              <div className="text-xs text-gray-500">{placesTxt(r)} · expected {r.expected ?? '—'} · written {sign(r.written)} · {SHOP[r.platform || ''] || r.platform}</div>
              <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full ${ITEM_STATUS[r.status]?.[1] || 'bg-gray-800 text-gray-300'}`}>{ITEM_STATUS[r.status]?.[0] || r.status}</span>
              {r.holds.length > 0 && <div className="text-xs text-amber-300 mt-1">{holdsTxt(r)}</div>}
              {role() === 'admin' && (r.status === 'would_write' || r.status === 'review') && <button onClick={() => writeNow(r)} disabled={busy} className="mt-2 px-3 py-1.5 text-xs bg-green-800 hover:bg-green-700 text-white rounded-md">{r.status === 'review' ? 'Retry' : 'Write now'} {sign(r.delta)}</button>}
            </div>
          )}>
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-left">Shop</th><th className="px-3 py-2 text-left">Counted where</th>
                  <th className="px-3 py-2 text-center">Counted</th><th className="px-3 py-2 text-center">Expected</th><th className="px-3 py-2 text-center">Written</th>
                  <th className="px-3 py-2 text-center">To write</th><th className="px-3 py-2 text-left">Status / why</th><th className="px-3 py-2"></th>
                </tr></thead>
                <tbody>
                  {items.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-gray-500">Nothing here</td></tr>}
                  {items.map(r => (
                    <tr key={r.wms_id} className="border-b border-gray-800/70 align-top">
                      <td className="px-3 py-2 text-gray-200">{r.label}{r.size ? <span className="text-gray-500"> · {r.size}</span> : null}{r.home ? <div className="text-[11px] text-gray-500">home {r.home}</div> : null}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{SHOP[r.platform || ''] || r.platform}</td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{placesTxt(r)}</td>
                      <td className="px-3 py-2 text-center text-white">{r.counted ?? '—'}</td>
                      <td className="px-3 py-2 text-center text-gray-300">{r.expected ?? '—'}</td>
                      <td className="px-3 py-2 text-center text-gray-400">{r.written ? sign(r.written) : ''}{r.pending ? <span className="text-gray-500"> ({sign(r.pending)} sending)</span> : null}</td>
                      <td className={`px-3 py-2 text-center font-bold ${r.delta > 0 ? 'text-blue-300' : r.delta < 0 ? 'text-red-300' : 'text-gray-500'}`}>{r.delta ? sign(r.delta) : ''}</td>
                      <td className="px-3 py-2 text-xs"><span className={`px-1.5 py-0.5 rounded-full ${ITEM_STATUS[r.status]?.[1] || 'bg-gray-800 text-gray-300'}`}>{ITEM_STATUS[r.status]?.[0] || r.status}</span>
                        {r.holds.length > 0 && <div className="text-amber-300 mt-1">{holdsTxt(r)}</div>}
                        {r.writes.length > 0 && <div className="text-gray-500 mt-1">{r.writes.map(w => `${sign(w.delta)} ${w.status}${w.reason ? ` (${w.reason})` : ''} · ${w.by || ''}`).join(' · ')}</div>}</td>
                      <td className="px-3 py-2 text-right">{role() === 'admin' && (r.status === 'would_write' || r.status === 'review') &&
                        <button onClick={() => writeNow(r)} disabled={busy} className="px-2 py-1 text-xs bg-green-800 hover:bg-green-700 text-white rounded-md whitespace-nowrap">{r.status === 'review' ? 'Retry' : 'Write now'}</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PhoneCards>
        </>
      )}

      {tab === 'settings' && (
        <div className="max-w-2xl space-y-4 text-sm text-gray-300">
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div><div className="text-white font-semibold">Shop writes at Save count</div>
                <div className="text-xs text-gray-500 mt-0.5">{writes?.mode === 'on' ? 'ON — every Save count writes its ready items to the shops.' : 'OFF — Save count only records what would be written.'}{writes?.set_by ? ` · set by ${writes.set_by}` : ''}</div></div>
              {role() === 'admin' && (writes?.mode === 'on'
                ? <button onClick={() => setMode('off')} disabled={busy} className="px-4 py-2 bg-red-800 hover:bg-red-700 text-white rounded-lg">Switch OFF</button>
                : <button onClick={() => setMode('on')} disabled={busy || !writes?.first_write} className="px-4 py-2 bg-green-700 hover:bg-green-600 disabled:opacity-40 text-white rounded-lg">Switch ON</button>)}
            </div>
            <div className="text-xs mt-3">{writes?.first_write
              ? <span className="text-green-300">First write made: item {writes.first_write.wms_id}, {sign(writes.first_write.delta)} by {writes.first_write.by} ({fmtDate(writes.first_write.at)})</span>
              : <span className="text-amber-300">No write yet — make the first one by hand (Items → Write now on one item, watched in the shop's stock log). The switch can go ON after it.</span>}</div>
          </div>
        </div>
      )}

      {tab === 'misplaced' && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={misFixed} onChange={e => setMisFixed(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5">
              <option value="false">Open</option><option value="true">Resolved</option><option value="">All</option>
            </select>
            <span className="text-xs text-gray-500">Move home = carried to its shelf (credited there) · Found here = it LIVES here now (shelf written, pushed to WGR) · Restock shelf = one more place for it (its restock location)</span>
          </div>
          <PhoneCards rows={mis} keyOf={m => m.line_id} card={m => (
            <div>
              <div className="text-white font-semibold">{m.label}{m.size ? ` · ${m.size}` : ''} <span className="text-xs text-gray-500">×{m.qty} · {SHOP[m.platform] || m.platform}</span></div>
              <div className="text-xs text-gray-400">found at <b className="text-yellow-300">{m.found_at}</b> · belongs to <b className="text-white">{m.belongs_to || '—'}</b> · {m.found_by || '—'}</div>
              <div className="text-xs text-gray-500 mt-1">{m.hint}</div>
              {!m.fixed ? <div className="flex gap-2 mt-2 flex-wrap">
                <button onClick={() => resolve(m, 'move_home')} disabled={busy || !m.belongs_to} className="px-3 py-1.5 text-xs bg-green-800 hover:bg-green-700 disabled:opacity-40 text-white rounded-md">Move home</button>
                <button onClick={() => resolve(m, 'found_here')} disabled={busy} className="px-3 py-1.5 text-xs bg-blue-800 hover:bg-blue-700 text-white rounded-md">Found here</button>
                <button onClick={() => resolve(m, 'overflow')} disabled={busy || !m.belongs_to} className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-md">Restock shelf</button>
              </div> : <div className="text-xs text-green-400 mt-1">✓ {m.resolution} · {m.resolved_by}</div>}
            </div>
          )}>
            <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Found at</th><th className="px-3 py-2 text-left">Belongs to</th><th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-left">Found by</th><th className="px-3 py-2 text-left">Hint</th><th className="px-3 py-2 text-right"></th>
                </tr></thead>
                <tbody>
                  {mis.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-500">No misplaced pieces</td></tr>}
                  {mis.map(m => (
                    <tr key={m.line_id} className="border-b border-gray-800/70">
                      <td className="px-3 py-2 text-gray-400">{fmtDate(m.created_at)}</td>
                      <td className="px-3 py-2 text-yellow-300 font-semibold">{m.found_at}</td>
                      <td className="px-3 py-2 text-white">{m.belongs_to || '—'}</td>
                      <td className="px-3 py-2 text-gray-300">{m.label}{m.size ? ` · ${m.size}` : ''} <span className="text-xs text-gray-500">{SHOP[m.platform] || m.platform}</span></td>
                      <td className="px-3 py-2 text-center text-white">{m.qty}</td>
                      <td className="px-3 py-2 text-gray-400">{m.found_by || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{m.hint}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {m.fixed ? <span className="text-green-400 text-xs">✓ {m.resolution} · {m.resolved_by}</span> : <>
                          <button onClick={() => resolve(m, 'move_home')} disabled={busy || !m.belongs_to} className="px-2 py-1 text-xs bg-green-800 hover:bg-green-700 disabled:opacity-40 text-white rounded-md mr-1">Move home</button>
                          <button onClick={() => resolve(m, 'found_here')} disabled={busy} className="px-2 py-1 text-xs bg-blue-800 hover:bg-blue-700 text-white rounded-md mr-1">Found here</button>
                          <button onClick={() => resolve(m, 'overflow')} disabled={busy || !m.belongs_to} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-md">Restock shelf</button></>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PhoneCards>
        </>
      )}

      {tab === 'unlocated' && unl && (
        <div className="space-y-6 text-sm">
          <p className="text-xs text-gray-500">{unl.meaning}</p>
          {[['Stock at a code the floor plan cannot place', unl.odd_codes], ['Stock with no shelf at all', unl.no_shelf], ['Big Oliver rows that are not Motley\'s WGR stock', unl.big_oliver_without_twin]].map(([title, list]: any) => (
            <div key={title}>
              <h2 className="font-semibold text-white mb-2">{title} ({list.length})</h2>
              <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
                <table className="w-full text-sm"><thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Shelf</th><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2 text-left">Size</th><th className="px-3 py-2 text-left">Barcode</th><th className="px-3 py-2 text-center">Stock</th></tr></thead>
                  <tbody>{list.length === 0 && <tr><td colSpan={5} className="text-center py-5 text-gray-500">None</td></tr>}
                    {list.map((it: any) => <tr key={it.wms_id} className="border-b border-gray-800/70"><td className="px-3 py-1.5 text-gray-300">{it.stock_location || '—'}</td><td className="px-3 py-1.5 text-white">{it.picklist_number || it.article_number || it.name}</td><td className="px-3 py-1.5 text-gray-400">{it.size || ''}</td><td className="px-3 py-1.5 text-gray-500 font-mono text-xs">{it.barcode || ''}</td><td className="px-3 py-1.5 text-center text-white">{it.stock_qty}</td></tr>)}
                  </tbody></table>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'sessions' && (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
              <th className="px-3 py-2 text-left">Person</th><th className="px-3 py-2 text-left">Device</th><th className="px-3 py-2 text-left">Section</th><th className="px-3 py-2 text-left">Started</th>
              <th className="px-3 py-2 text-center">Minutes</th><th className="px-3 py-2 text-center">Scans</th><th className="px-3 py-2 text-center">Status</th><th></th></tr></thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-500">No sessions in this round</td></tr>}
              {sessions.map(s => (
                <tr key={s.id} className="border-b border-gray-800/70">
                  <td className="px-3 py-2 text-gray-200">{s.person}</td><td className="px-3 py-2 text-gray-500 text-xs">{s.device || '—'}</td>
                  <td className="px-3 py-2 text-white">{s.display}</td><td className="px-3 py-2 text-gray-400">{fmtDate(s.started_at)}</td>
                  <td className="px-3 py-2 text-center text-gray-300">{s.minutes}</td><td className="px-3 py-2 text-center text-gray-300">{s.scans}</td>
                  <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'saved' ? 'bg-green-900/60 text-green-300' : s.status === 'active' ? (s.paused ? 'bg-amber-900/60 text-amber-300' : 'bg-blue-900/60 text-blue-300') : s.status === 'cancelled' ? 'bg-red-900/50 text-red-300' : 'bg-gray-700 text-gray-300'}`}
                    title={s.status === 'cancelled' ? `cancelled by ${s.cancelled_by || '?'}${s.cancel_note ? ` — ${s.cancel_note}` : ''}` : s.paused_minutes ? `${s.paused_minutes} min paused` : ''}>{s.status === 'active' && s.paused ? 'paused' : s.status}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{s.status === 'active' && <>
                    <button onClick={async () => { if (!window.confirm(`Save and close ${s.person}'s session on ${s.display}?\n\nThe section stays NOT done — nothing is written; it is finished by a count.`)) return; setBusy(true); try { await jpost(`${INV}/sessions/${s.id}/close`, {}); await loadSessions(); } catch (e: any) { setMsg(e.message); } setBusy(false); }} disabled={busy} className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded-md mr-1">Close (save)</button>
                    <button onClick={async () => { if (!window.confirm(`Cancel ${s.person}'s count on ${s.display}?\n\nEverything this count changed is undone; the section goes back to how it was before Start. The scans stay in the history.`)) return; setBusy(true); try { await jpost(`${INV}/sessions/${s.id}/cancel`, {}); await loadSessions(); } catch (e: any) { setMsg(e.message); } setBusy(false); }} disabled={busy} className="px-2 py-1 text-xs bg-red-900 hover:bg-red-800 text-white rounded-md">Cancel</button></>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'rounds' && (
        <div className="space-y-2">
          {rounds.map(rd => {
            const v = rd.stats?.volume || {}, i = rd.stats?.issues || {}, sp = rd.stats?.speed || {};
            const num = (x: any) => (x === null || x === undefined ? '—' : Number(x).toLocaleString());
            return (
              <div key={rd.id} className="bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-white font-semibold">Round {rd.id}{rd.label ? ` · ${rd.label}` : ''} <span className={`text-xs px-2 py-0.5 rounded-full ml-2 ${rd.status === 'open' ? 'bg-blue-900/60 text-blue-300' : 'bg-gray-700 text-gray-300'}`}>{rd.status}</span></span>
                  <span className="text-gray-500 text-xs">{fmtDate(rd.started_at)} → {rd.status === 'open' ? 'now' : fmtDate(rd.ended_at)}{rd.closed_by ? ` · closed by ${rd.closed_by}` : ''}</span>
                </div>
                {rd.stats && <div className="text-xs text-gray-400 mt-1">counted {num(v.items_counted)} of {num(v.items_to_count)} · sections {num(v.sections_counted)}/{num(v.sections_to_count)} · hours {num(sp.hours_worked)} · missing {num(i.items_missing)} · surplus {num(i.items_surplus)} · wrong section {num(i.items_wrong_section)} · writes {num(i.written)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
